import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  FiArrowRight,
  FiArrowLeft,
  FiBriefcase,
  FiChevronDown,
  FiChevronRight,
  FiChevronUp,
  FiCheckCircle,
  FiClock,
  FiColumns,
  FiExternalLink,
  FiEdit2,
  FiDownload,
  FiFile,
  FiFileText,
  FiFlag,
  FiHelpCircle,
  FiInbox,
  FiLock,
  FiLogOut,
  FiList,
  FiMail,
  FiMessageSquare,
  FiMove,
  FiPaperclip,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiSend,
  FiShield,
  FiUploadCloud,
  FiUser,
  FiX,
} from "react-icons/fi";
import riomaxWordmark from "../assets/brands/riomax-360-logo.png";
import {
  mentionsFromRichText,
  ProjectCommentBody,
  ProjectRichText,
  RichTextEditor,
  stripProjectRichText,
} from "../HRMPanel/newHrm/screens/ProjectsScreen";
import "../Components/css/SowPortal.css";
import RiomaxPlanStudio from "./RiomaxPlanStudio";
import { workspaceTargetFromUrl } from "./dealDesk/workspaceLinks";
import PlanStudioRecovery from "./PlanStudioRecovery";
import DealDeskShell, { DEAL_DESK_APPS, workspaceAppFromUrl } from "./dealDesk/DealDeskShell";
import WorkspaceResources from "./dealDesk/WorkspaceResources";
import WorkspaceProjects from "./dealDesk/WorkspaceProjects";
import DealDeskAi from "./dealDesk/DealDeskAi";
import DealDeskNotifications from "./dealDesk/DealDeskNotifications";

const antrazalWordmark = "/assets/antrazal-brand.svg";

const STORIES = [
  {
    customer: "24 Hour Home Care",
    title: "A rebuild that made the caregiver's day simpler",
    image: "https://d2lyw5vkb9es5b.cloudfront.net/24_hr_story_banner.webp",
    url: "/customer-stories/24-hour-home-care-workforce-modernization",
  },
  {
    customer: "trivago",
    title: "Support portal that lets hoteliers keep pace",
    image: "/assets/customer-stories/trivago/hero.webp",
    url: "/customer-stories/trivago-support-portal",
  },
  {
    customer: "Cambri",
    title: "Making ARR easier to explain",
    image: "/assets/customer-stories/cambri/08_background.png",
    url: "/customer-stories/making-arr-easier-to-explain",
  },
];

const SOW_PROJECT_CONFIG = Object.freeze({
  jacobs: {
    brandName: "Jacobs",
    workspaceName: "Jacobs engagement workspace",
    pageTitle: "Antrazal | Jacobs | Engagement Workspace",
    protectedAssets: {
      "jacobs-full-sow-pack": {
        fileName: "Antrazal_Jacobs_Flood_Portal_Consolidated_Pack_v3.0.8.2.pdf",
        contentType: "application/pdf",
      },
      "antrazal-way-governance-guide": {
        fileName: "The_Antrazal_Way_Governance_Security_Privacy_Delivery_Assurance_Guide.pdf",
        contentType: "application/pdf",
      },
      "antrazal-msa-template": {
        fileName: "Antrazal_MSA_Template.pdf",
        contentType: "application/pdf",
      },
    },
    maximumExportAssets: 3,
  },
  riomax: {
    brandName: "Riomax",
    workspaceName: "Riomax engagement workspace",
    pageTitle: "Antrazal | Riomax | Engagement Workspace",
    protectedAssets: {
      "riomax-phase-1-sow": {
        fileName: "Antrazal_Riomax_SOW_Updated.pdf",
        contentType: "application/pdf",
      },
      "riomax-msa": {
        fileName: "Antrazal_Riomax_MSA_Updated.pdf",
        contentType: "application/pdf",
      },
    },
    maximumExportAssets: 2,
  },
});

const getBackendBaseUrl = () => {
  if (
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1)$/.test(window.location.hostname) &&
    window.location.port !== "3000"
  ) {
    return "http://localhost:3000";
  }
  return "";
};

const getRequestedProjectSlug = () => {
  if (typeof window === "undefined") return "jacobs";
  return window.location.pathname.toLowerCase().startsWith("/e/riomax")
    ? "riomax"
    : "jacobs";
};

const readPayload = async (response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const requestWorkspaceJson = async (path, options = {}) => {
  const { projectSlug = getRequestedProjectSlug(), isSessionCurrent, ...fetchOptions } = options;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = { ...(options.headers || {}) };
  if (!isFormData && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  if (!headers["X-SOW-Project"] && !headers["x-sow-project"]) {
    headers["X-SOW-Project"] = projectSlug;
  }
  const response = await fetch(`${getBackendBaseUrl()}${path}`, {
    cache: "no-store",
    credentials: "include",
    ...fetchOptions,
    headers,
  });
  const payload = await readPayload(response);
  if (!response.ok || payload?.success === false || payload?.error === true) {
    const message = Array.isArray(payload?.message) ? payload.message.join(" ") : payload?.message;
    const error = new Error(message || "The secure workspace could not be reached.");
    error.status = response.status;
    if (
      response.status === 401 &&
      !path.includes("/auth/") &&
      !path.includes("/session") &&
      (!isSessionCurrent || isSessionCurrent()) &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(new CustomEvent("sow:session-expired"));
    }
    throw error;
  }
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error("The workspace returned an invalid response. Refresh and try again.");
  }
  return payload;
};

const formatCaseDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const caseStatusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "closed") return "is-closed";
  if (normalized === "working" || normalized === "review") return "is-active";
  return "is-pending";
};

const PHASE_TWO_STATUSES = ["Defined", "Ready To Quote"];
const PHASE_TWO_PRIORITIES = ["High", "Medium", "Low"];

const normalizePhaseTwoStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "ready to quote" || /approved|complete|closed|working|review/.test(normalized)) {
    return "Ready To Quote";
  }
  return "Defined";
};

const getInitials = (name = "") =>
  String(name || "A")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const phaseTwoArray = (value) => (Array.isArray(value) ? value : []);

const normalizePhaseTwoFile = (file = {}) => ({
  id: String(file.id || file.fileId || file.contentDocumentId || file.documentId || ""),
  name: String(file.name || file.fileName || file.title || "Attachment"),
  size: Number(file.size || file.contentSize || file.fileSize || 0),
  type: String(file.type || file.fileType || file.contentType || ""),
  createdAt: file.createdAt || file.uploadedAt || file.createdDate || "",
  uploadedBy: String(file.uploadedBy || file.createdByName || file.author || ""),
});

const normalizePhaseTwoComment = (comment = {}, index = 0) => ({
  id: String(comment.id || comment.commentId || `comment-${index}`),
  body: String(comment.body || comment.comment || comment.commentBody || comment.text || ""),
  author: String(comment.author?.name || comment.author || comment.createdByName || comment.userName || "Project team"),
  authorType: String(comment.authorType || comment.source || "team").toLowerCase(),
  createdAt: comment.createdAt || comment.createdDate || comment.updatedAt || "",
  mentions: phaseTwoArray(comment.mentions || comment.mentioned).map((mention) => ({
    id: String(mention?.id || mention?.contactId || mention || ""),
    name: String(mention?.name || mention?.contactName || ""),
    email: String(mention?.email || ""),
  })).filter((mention) => mention.id || mention.email),
});

const normalizePhaseTwoItem = (item = {}, index = 0) => {
  const comments = phaseTwoArray(item.comments?.items || item.comments || item.answers).map(normalizePhaseTwoComment);
  const files = phaseTwoArray(item.files?.items || item.files || item.attachments).map(normalizePhaseTwoFile);
  return {
    id: String(item.id || item.caseId || item.recordId || `phase-two-${index}`),
    key: String(item.key || item.caseNumber || item.reference || item.number || ""),
    title: String(item.title || item.subject || item.name || "Untitled Phase 2 item"),
    description: String(item.description || item.content || item.question || ""),
    status: normalizePhaseTwoStatus(item.status),
    priority: PHASE_TWO_PRIORITIES.includes(item.priority) ? item.priority : "Medium",
    sortOrder: Number.isFinite(Number(item.sortOrder)) && Number(item.sortOrder) > 0
      ? Number(item.sortOrder)
      : index + 1,
    clientApproved: Boolean(item.clientApproved ?? item.approvedForScope ?? item.scopeStatus === "Approved for scope"),
    assignee: item.assignee?.id || item.assigneeId
      ? {
        id: String(item.assignee?.id || item.assigneeId || ""),
        name: String(item.assignee?.name || item.assigneeName || "Assigned team member"),
        email: String(item.assignee?.email || item.assigneeEmail || ""),
      }
      : null,
    createdAt: item.createdAt || item.createdDate || "",
    updatedAt: item.updatedAt || item.lastModifiedDate || item.createdAt || "",
    createdBy: String(item.createdByName || item.createdBy || item.ownerName || "Project team"),
    comments,
    files,
    commentCount: Number(item.commentCount ?? comments.length),
    attachmentCount: Number(item.attachmentCount ?? files.length),
  };
};

const phaseTwoAssigneesFromPayload = (payload) => {
  const data = payload?.data ?? payload ?? {};
  return phaseTwoArray(data.assignees).map((assignee) => ({
    id: String(assignee.id || assignee.contactId || ""),
    name: String(assignee.name || assignee.contactName || "Team member"),
    email: String(assignee.email || ""),
  })).filter((assignee) => assignee.id);
};

const phaseTwoMembersFromPayload = (payload) => {
  const data = payload?.data ?? payload ?? {};
  return phaseTwoArray(data.members || data.mentionablePeople).map((member) => ({
    id: String(member.id || member.contactId || ""),
    name: String(member.name || member.contactName || "Project member"),
    email: String(member.email || ""),
  })).filter((member) => member.id);
};

const phaseTwoRowsFromPayload = (payload) => {
  const data = payload?.data ?? payload ?? {};
  const rows = Array.isArray(data)
    ? data
    : data.items || data.phaseTwoItems || data.cases || data.records || [];
  return phaseTwoArray(rows).map(normalizePhaseTwoItem);
};

const phaseTwoItemFromPayload = (payload) => {
  const data = payload?.data ?? payload ?? {};
  const row = data.item || data.phaseTwoItem || data.case || data.record || null;
  return row ? normalizePhaseTwoItem(row) : null;
};

const formatFileSize = (value) => {
  const bytes = Number(value || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 102.4) / 10} KB`;
  return `${Math.round(bytes / (1024 * 102.4)) / 10} MB`;
};

const fileNameFromDisposition = (headerValue, fallback) => {
  const encoded = String(headerValue || "").match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  return String(headerValue || "").match(/filename="?([^";]+)"?/i)?.[1] || fallback;
};

function JacobsWordmark({ className = "" }) {
  return (
    <svg
      className={className}
      width="73"
      height="18"
      viewBox="0 0 73 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Jacobs"
    >
      <path
        d="M67.75 17.0832C70.792 17.0832 72.911 15.5094 72.911 13.2172C72.911 11.2506 71.7584 10.0913 69.3385 9.58327L67.8182 9.25915C67.0581 9.09865 66.6439 8.7496 66.6439 8.31016C66.6439 7.71023 67.2195 7.33936 68.1657 7.33936C69.2237 7.33936 70.2382 7.63855 71.4373 8.33354L72.4736 5.94938C71.3908 5.16245 69.9155 4.72302 68.2805 4.72302C65.3067 4.72302 63.3708 6.25013 63.3708 8.63584C63.3708 10.5338 64.5001 11.6449 66.8983 12.1295L68.3022 12.4302C69.2469 12.639 69.5696 12.918 69.5696 13.4712C69.5696 14.0742 68.9026 14.4217 67.7034 14.4217C66.7122 14.4217 65.2602 13.8888 64.0394 13.08L63.0264 15.4876C64.0626 16.4599 65.8605 17.0599 67.75 17.0832ZM56.1124 14.1895C54.4293 14.1895 53.348 12.9632 53.348 10.9031C53.348 8.84309 54.4293 7.61517 56.1124 7.61517C57.7955 7.61517 58.8767 8.84309 58.8767 10.9031C58.8767 12.9632 57.7955 14.1895 56.1124 14.1895ZM56.8725 17.0832C59.6601 17.0832 62.1484 14.8159 62.1484 10.9031C62.1484 7.10718 59.8913 4.72302 56.9873 4.72302C55.4205 4.72302 54.2229 5.39464 53.4861 6.50568V-0.000101089H50.306V13.4945C50.306 14.6071 50.2595 15.5795 50.1679 16.7591H53.1619C53.2084 16.2963 53.255 15.8117 53.2782 15.2788C54.1314 16.4817 55.4438 17.0832 56.8725 17.0832ZM42.7234 14.1895C41.0418 14.1895 39.9591 12.9632 39.9591 10.9031C39.9591 8.84309 41.0418 7.61517 42.7234 7.61517C44.4065 7.61517 45.4909 8.84309 45.4909 10.9031C45.4909 12.9632 44.4065 14.1895 42.7234 14.1895ZM42.6086 17.0832C46.2044 17.0832 48.7625 14.6991 48.7625 10.9031C48.7625 7.10718 46.3192 4.72302 42.7234 4.72302C39.1989 4.72302 36.6859 7.31599 36.6859 10.9031C36.6859 14.5822 39.0826 17.0832 42.6086 17.0832ZM32.1469 17.0832C33.7354 17.0832 35.3735 16.5285 36.3632 15.6247L35.2122 13.1953C34.3357 13.7501 33.2079 14.121 32.3315 14.121C30.532 14.121 29.3825 12.8946 29.3825 10.9031C29.3825 8.98178 30.5103 7.6853 32.4913 7.6853C33.3227 7.6853 34.2892 8.05616 35.0958 8.58909L36.3632 6.06469C35.3502 5.18583 33.9681 4.72302 32.423 4.72302C28.4363 4.72302 26.1094 7.56843 26.1094 10.9031C26.1094 14.6991 28.413 17.0832 32.1469 17.0832ZM21.4773 12.5237C21.4773 14.0742 20.1634 14.5822 19.1038 14.5822C18.0211 14.5822 17.4207 14.1677 17.4207 13.2873C17.4207 12.5237 17.9063 11.969 19.2202 11.8989L21.4773 11.782V12.5237ZM22.0063 16.7591H25.0033C24.7722 15.7883 24.6108 14.1895 24.6108 13.08V9.35265C24.6108 6.48231 22.8362 4.72302 19.7042 4.72302C16.3147 4.72302 14.841 6.87499 14.6114 8.58909H17.6053C17.7915 7.82398 18.4585 7.33936 19.6095 7.33936C20.7404 7.33936 21.4773 7.91748 21.4773 9.07527V9.56145L18.5733 9.70014C15.508 9.8622 14.2639 11.4127 14.2639 13.4945C14.2639 15.7416 15.8539 17.0832 18.2739 17.0832C20.0718 17.0832 21.2244 16.2059 21.7999 15.4876C21.8465 15.9722 21.938 16.435 22.0063 16.7591ZM9.49529 6.41375H12.8584V-0.000101089H6.29193V3.19436H9.49529V6.41375ZM6.36019 17.0832C10.2321 17.0832 12.8584 14.3531 12.8584 10.5556V8.54235H9.49529V10.5556C9.49529 12.5471 8.2729 13.8436 6.36019 13.8436C4.56227 13.8436 3.52603 12.7076 3.34143 11.1119H0C0.184601 14.6071 2.6961 17.0832 6.36019 17.0832Z"
        fill="#141414"
      />
    </svg>
  );
}

function BrandWordmark({ alt, className = "", fallback, src }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span
        aria-label={alt}
        className={`${className} sow-brand-wordmark-fallback`}
        role="img"
      >
        {fallback}
      </span>
    );
  }

  return <img alt={alt} className={className} onError={() => setFailed(true)} src={src} />;
}

function ProjectWordmark({ className = "", projectSlug }) {
  if (projectSlug === "riomax") {
    return (
      <BrandWordmark
        alt="Riomax 360"
        className={`${className} sow-riomax-wordmark`}
        fallback="Riomax 360"
        src={riomaxWordmark}
      />
    );
  }
  return <JacobsWordmark className={className} />;
}

function CaseWorkspace({
  cases,
  comment,
  commentBusy,
  loading,
  onBack,
  onCommentChange,
  onNewQuestion,
  onRefresh,
  onSelectCase,
  onSubmitComment,
  selectedCaseId,
  supportError,
}) {
  const selectedCase = cases.find((caseRow) => caseRow.id === selectedCaseId) || cases[0] || null;

  return (
    <section className="sow-case-workspace" role="dialog" aria-modal="true" aria-labelledby="sow-case-workspace-title">
      <header className="sow-case-workspace__header">
        <div className="sow-case-workspace__title">
          <button type="button" onClick={onBack} aria-label="Back to statement of work">
            <FiArrowLeft aria-hidden="true" />
          </button>
          <div>
            <span>Project support</span>
            <h1 id="sow-case-workspace-title">Questions and Cases</h1>
          </div>
        </div>
        <div className="sow-case-workspace__commands">
          <button type="button" className="sow-case-refresh" onClick={onRefresh} disabled={loading} aria-label="Refresh" title="Refresh cases">
            <FiRefreshCw className={loading ? "sow-spin" : ""} aria-hidden="true" /><span>Refresh</span>
          </button>
          <button type="button" className="sow-case-new" onClick={onNewQuestion} aria-label="New question" title="New question">
            <FiMessageSquare aria-hidden="true" /><span>New question</span>
          </button>
        </div>
      </header>

      {supportError && <div className="sow-case-workspace__error" role="alert">{supportError}</div>}
      <div className="sow-case-workspace__layout">
        <aside className="sow-case-workspace__list" aria-label="Project cases">
          <div className="sow-case-workspace__list-heading">
            <strong>Project cases</strong>
            <span>{cases.length}</span>
          </div>
          {loading && !cases.length ? (
            <div className="sow-case-workspace__empty"><FiRefreshCw className="sow-spin" aria-hidden="true" /> Loading cases</div>
          ) : !cases.length ? (
            <div className="sow-case-workspace__empty"><FiInbox aria-hidden="true" /><strong>No cases yet</strong><span>Ask a question to start a project conversation.</span></div>
          ) : (
            <div className="sow-case-workspace__rows">
              {cases.map((caseRow) => (
                <button
                  type="button"
                  key={caseRow.id}
                  className={caseRow.id === selectedCase?.id ? "is-selected" : ""}
                  onClick={() => onSelectCase(caseRow.id)}
                >
                  <span className="sow-case-number">Case {caseRow.caseNumber}</span>
                  <strong>{caseRow.subject}</strong>
                  <small>Updated {formatCaseDate(caseRow.updatedAt)}</small>
                  <em className={`sow-case-status ${caseStatusClass(caseRow.status)}`}>{caseRow.status}</em>
                </button>
              ))}
            </div>
          )}
        </aside>

        <main className="sow-case-workspace__detail">
          {selectedCase ? (
            <>
              <header className="sow-case-detail__header">
                <div>
                  <span>Case {selectedCase.caseNumber}</span>
                  <h2>{selectedCase.subject}</h2>
                </div>
                <em className={`sow-case-status ${caseStatusClass(selectedCase.status)}`}>{selectedCase.status}</em>
              </header>
              <div className="sow-case-detail__thread" aria-label="Case conversation">
                <article className="sow-case-message is-client">
                  <div className="sow-case-message__avatar">{getInitials("You")}</div>
                  <div>
                    <header><strong>You</strong><time>{formatCaseDate(selectedCase.createdAt)}</time></header>
                    <p>{selectedCase.question}</p>
                  </div>
                </article>
                {(selectedCase.answers || []).map((answer) => (
                  <article className={`sow-case-message ${answer.authorType === "client" ? "is-client" : "is-team"}`} key={answer.id}>
                    <div className="sow-case-message__avatar">{getInitials(answer.author)}</div>
                    <div>
                      <header><strong>{answer.author}</strong><time>{formatCaseDate(answer.createdAt)}</time></header>
                      <p>{answer.body}</p>
                    </div>
                  </article>
                ))}
                {!selectedCase.answers?.length && (
                  <div className="sow-case-detail__pending"><FiClock aria-hidden="true" /> The project team has your question. Replies will appear in this thread.</div>
                )}
              </div>
              <form className="sow-case-reply" onSubmit={(event) => onSubmitComment(event, selectedCase.id)}>
                <label htmlFor="sow-case-reply">Add a comment</label>
                <textarea
                  id="sow-case-reply"
                  value={comment}
                  onChange={(event) => onCommentChange(event.target.value)}
                  placeholder="Write a clear project update or follow-up question."
                  maxLength={3500}
                  disabled={commentBusy}
                  required
                />
                <div>
                  <span>{comment.length.toLocaleString("en-IN")} / 3,500</span>
                  <button type="submit" disabled={commentBusy || !comment.trim()}>
                    {commentBusy ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiSend aria-hidden="true" />}
                    {commentBusy ? "Adding comment" : "Add comment"}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="sow-case-workspace__detail-empty"><FiBriefcase aria-hidden="true" /><strong>No case selected</strong><span>Select a case or ask a new question.</span></div>
          )}
        </main>
      </div>
    </section>
  );
}

function PhaseTwoWorkspace({ currentUser, onClose, embedded = false, active = true, notificationTarget }) {
  const requestJson = useCallback((path, options = {}) =>
    requestWorkspaceJson(path, { ...options, projectSlug: "riomax", isSessionCurrent: () => mountedRef.current }), []);
  const [items, setItems] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [pendingActions, setPendingActions] = useState({});
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [comment, setComment] = useState("");
  const [commentMentions, setCommentMentions] = useState([]);
  const [commenting, setCommenting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newPriority, setNewPriority] = useState("Medium");
  const [downloadingFileId, setDownloadingFileId] = useState("");
  const [draggedItemId, setDraggedItemId] = useState("");
  const [dragOverStatus, setDragOverStatus] = useState("");
  const [dragTargetItemId, setDragTargetItemId] = useState("");
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const createButtonRef = useRef(null);
  const workspaceRef = useRef(null);
  const createDialogRef = useRef(null);
  const detailTitleRef = useRef(null);
  const returnFocusItemIdRef = useRef("");
  const loadRequestRef = useRef(0);
  const loadControllerRef = useRef(null);
  const pendingActionsRef = useRef(new Map());
  const detailRevisionRef = useRef(0);
  const mountedRef = useRef(true);
  const busyAction = pendingActions[selectedItemId] || pendingActions.workspace || "";
  const hasPendingActions = Object.keys(pendingActions).length > 0;

  const beginAction = (action, itemId = "workspace") => {
    if (pendingActionsRef.current.has(itemId) || pendingActionsRef.current.has("workspace")
      || (itemId === "workspace" && pendingActionsRef.current.size)) return false;
    pendingActionsRef.current.set(itemId, action);
    setPendingActions(Object.fromEntries(pendingActionsRef.current));
    // A read started before this write must not replace the newly saved record.
    loadRequestRef.current += 1;
    loadControllerRef.current?.abort();
    setLoading(false);
    return true;
  };

  const finishAction = (itemId = "workspace") => {
    pendingActionsRef.current.delete(itemId);
    if (mountedRef.current) setPendingActions(Object.fromEntries(pendingActionsRef.current));
  };

  const loadItems = React.useCallback(async (preferredItemId = "", silent = false) => {
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    if (!silent) setLoading(true);
    setError("");
    try {
      const payload = await requestJson("/backend/sow/phase-two", { signal: controller.signal });
      if (requestId !== loadRequestRef.current) return;
      const rows = phaseTwoRowsFromPayload(payload);
      setItems(rows);
      setAssignees(phaseTwoAssigneesFromPayload(payload));
      setMembers(phaseTwoMembersFromPayload(payload));
      setSelectedItemId((current) => {
        const nextId = preferredItemId || current;
        return rows.some((item) => item.id === nextId) ? nextId : "";
      });
    } catch (requestError) {
      if (requestId === loadRequestRef.current && requestError.name !== "AbortError") setError(requestError.message);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, [requestJson]);

  useEffect(() => {
    mountedRef.current = true;
    loadItems();
    return () => {
      mountedRef.current = false;
      loadRequestRef.current += 1;
      loadControllerRef.current?.abort();
    };
  }, [loadItems]);

  const selectedItem = items.find((item) => item.id === selectedItemId) || null;
  const focusedNotification = useRef(null);
  const highlightedNotification = useRef(null);
  useEffect(() => {
    if (!active || !notificationTarget || focusedNotification.current === notificationTarget.requestId) return;
    if (editing || comment.trim() || creating || hasPendingActions) {
      setError("Finish the current draft before opening a notification."); return;
    }
    focusedNotification.current = notificationTarget.requestId;
    loadItems(notificationTarget.recordId);
    setSelectedItemId(notificationTarget.recordId); setDetailOpen(true);
    setFilterText(""); setStatusFilter("All");
  }, [active, notificationTarget, loadItems, editing, comment, creating, hasPendingActions]);
  useEffect(() => {
    if (!active || !notificationTarget || loading || selectedItemId !== notificationTarget.recordId
      || highlightedNotification.current === notificationTarget.requestId) return;
    const frame = requestAnimationFrame(() => {
      const row = [...(workspaceRef.current?.querySelectorAll("[data-phase-two-comment]") || [])].find(element => element.dataset.phaseTwoComment === notificationTarget.commentId);
      if (row) {
        workspaceRef.current.querySelectorAll('[data-notification-highlight]').forEach(element => element.removeAttribute('data-notification-highlight'));
        highlightedNotification.current = notificationTarget.requestId;
        row.dataset.notificationHighlight = "true"; row.tabIndex = -1; row.scrollIntoView({ block: "center" }); row.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [active, notificationTarget, loading, selectedItemId, items]);
  const selectedAssigneeIsEligible = !selectedItem?.assignee
    || assignees.some((assignee) => assignee.id === selectedItem.assignee.id);
  const searchedItems = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      `${item.key} ${item.title} ${item.status} ${item.priority} ${item.assignee?.name || ""}`.toLowerCase().includes(query),
    );
  }, [filterText, items]);
  const statusCounts = useMemo(() => Object.fromEntries(
    PHASE_TWO_STATUSES.map((status) => [
      status,
      searchedItems.filter((item) => item.status === status).length,
    ]),
  ), [searchedItems]);
  const visibleItems = useMemo(() => (
    statusFilter === "All"
      ? searchedItems
      : searchedItems.filter((item) => item.status === statusFilter)
  ), [searchedItems, statusFilter]);
  const visibleItemsByStatus = useMemo(() => Object.fromEntries(
    PHASE_TWO_STATUSES.map((status) => [
      status,
      visibleItems
        .filter((item) => item.status === status)
        .sort((left, right) => left.sortOrder - right.sortOrder),
    ]),
  ), [visibleItems]);
  const visibleStatuses = statusFilter === "All" ? PHASE_TWO_STATUSES : [statusFilter];
  const orderedVisibleItems = visibleStatuses.flatMap((status) => visibleItemsByStatus[status] || []);
  const selectedLaneItems = selectedItem
    ? items
      .filter((item) => item.status === selectedItem.status)
      .sort((left, right) => left.sortOrder - right.sortOrder)
    : [];
  const selectedLaneIndex = selectedItem
    ? selectedLaneItems.findIndex((item) => item.id === selectedItem.id)
    : -1;

  const selectItem = (itemId, openDetail = true) => {
    detailRevisionRef.current += 1;
    returnFocusItemIdRef.current = itemId;
    setSelectedItemId(itemId);
    if (openDetail) setDetailOpen(true);
    setEditing(false);
    setComment("");
    setCommentMentions([]);
    setCommenting(false);
    setError("");
    if (openDetail) {
      window.setTimeout(() => detailTitleRef.current?.focus(), 0);
    }
  };

  const closeDetail = useCallback(() => {
    detailRevisionRef.current += 1;
    setDetailOpen(false);
    setViewMode("list");
    setEditing(false);
    setComment("");
    setCommentMentions([]);
    setCommenting(false);
    const itemId = returnFocusItemIdRef.current || selectedItemId;
    window.setTimeout(() => {
      const itemButton = document.querySelector(`[data-phase-two-item-id="${itemId}"]`);
      (itemButton || searchInputRef.current)?.focus();
    }, 0);
  }, [selectedItemId]);

  const showListView = () => {
    setViewMode("list");
    if (detailOpen) closeDetail();
  };

  const showSplitView = () => {
    const nextItem = selectedItem || orderedVisibleItems[0] || items[0] || null;
    setViewMode("split");
    if (nextItem) {
      selectItem(nextItem.id, true);
    }
  };

  const openCreate = () => {
    setCreating(true);
    setError("");
  };

  const closeCreate = useCallback(() => {
    if (pendingActionsRef.current.has("workspace")) return;
    setCreating(false);
    window.setTimeout(() => createButtonRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    const handleDialogKey = (event) => {
      if (!active) return;
      if (event.key === "Tab") {
        if (embedded && !creating) return;
        const activeDialog = creating ? createDialogRef.current : workspaceRef.current;
        const focusable = Array.from(activeDialog?.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
        ) || []).filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true");
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !activeDialog.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !activeDialog.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key !== "Escape") return;
      if (creating) {
        closeCreate();
      } else if (editing) {
        setEditing(false);
      } else if (commenting) {
        setComment("");
        setCommentMentions([]);
        setCommenting(false);
      } else if (detailOpen) {
        closeDetail();
      } else {
        onClose();
      }
    };
    document.addEventListener("keydown", handleDialogKey);
    return () => document.removeEventListener("keydown", handleDialogKey);
  }, [active, embedded, closeCreate, closeDetail, commenting, creating, detailOpen, editing, onClose]);

  const selectAdjacentItem = (event, itemId, direction) => {
    const currentIndex = orderedVisibleItems.findIndex((item) => item.id === itemId);
    const target = orderedVisibleItems[currentIndex + direction];
    if (!target) return;
    event.preventDefault();
    selectItem(target.id, false);
    window.setTimeout(() => {
      document.querySelector(`[data-phase-two-item-id="${target.id}"]`)?.focus();
    }, 0);
  };

  const startEditing = () => {
    if (!selectedItem) return;
    setEditTitle(selectedItem.title);
    setEditDescription(selectedItem.description);
    setEditing(true);
    setError("");
  };

  const saveItem = async (event) => {
    event.preventDefault();
    if (!selectedItem || !editTitle.trim()) return;
    if (!beginAction("save-item", selectedItem.id)) return;
    const revision = detailRevisionRef.current;
    setError("");
    try {
      const payload = await requestJson(`/backend/sow/phase-two/${encodeURIComponent(selectedItem.id)}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: editTitle.trim(),
          subject: editTitle.trim(),
          description: editDescription,
        }),
      });
      if (!mountedRef.current) return;
      if (revision === detailRevisionRef.current) setEditing(false);
      const updatedItem = phaseTwoItemFromPayload(payload);
      if (updatedItem) {
        setItems((current) => current.map((item) => (item.id === selectedItem.id ? updatedItem : item)));
      } else {
        await loadItems("", true);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      finishAction(selectedItem.id);
    }
  };

  const updateWorkflow = async (itemId, changes) => {
    const currentItem = items.find((item) => item.id === itemId);
    if (!currentItem || !beginAction(`workflow-${itemId}`, itemId)) return;
    const previousItem = currentItem;
    const nextAssignee = Object.prototype.hasOwnProperty.call(changes, "assigneeId")
      ? assignees.find((assignee) => assignee.id === changes.assigneeId) || null
      : currentItem.assignee;
    const optimisticChanges = {
      ...changes,
      ...(Object.prototype.hasOwnProperty.call(changes, "assigneeId") ? { assignee: nextAssignee } : {}),
      ...(changes.clientApproved === true ? { status: "Ready To Quote" } : {}),
    };
    delete optimisticChanges.assigneeId;
    setItems((current) => current.map((item) => (
      item.id === itemId ? { ...item, ...optimisticChanges } : item
    )));
    setError("");
    try {
      const payload = await requestJson(`/backend/sow/phase-two/${encodeURIComponent(itemId)}/workflow`, {
        method: "PATCH",
        body: JSON.stringify(changes),
      });
      const updatedItem = phaseTwoItemFromPayload(payload);
      if (!mountedRef.current) return;
      if (updatedItem) {
        setItems((current) => current.map((item) => (item.id === itemId ? updatedItem : item)));
      } else {
        await loadItems("", true);
      }
    } catch (requestError) {
      setItems((current) => current.map((item) => (item.id === itemId ? previousItem : item)));
      setError(requestError.message);
    } finally {
      finishAction(itemId);
    }
  };

  const startDraggingItem = (event, itemId) => {
    if (hasPendingActions) {
      event.preventDefault();
      return;
    }
    setDraggedItemId(itemId);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", itemId);
    }
  };

  const orderedItemsAfterDrop = (itemId, targetStatus, targetItemId = "", placeAfter = false) => {
    const draggedItem = items.find((item) => item.id === itemId);
    if (!draggedItem || (targetItemId && targetItemId === itemId)) return null;
    const lanes = Object.fromEntries(PHASE_TWO_STATUSES.map((status) => [
      status,
      items
        .filter((item) => item.status === status && item.id !== itemId)
        .sort((left, right) => left.sortOrder - right.sortOrder),
    ]));
    const targetLane = lanes[targetStatus];
    if (!targetLane) return null;
    const targetIndex = targetItemId
      ? targetLane.findIndex((item) => item.id === targetItemId)
      : targetLane.length;
    const insertAt = targetIndex < 0
      ? targetLane.length
      : Math.min(targetLane.length, targetIndex + (placeAfter ? 1 : 0));
    targetLane.splice(insertAt, 0, { ...draggedItem, status: targetStatus });
    return PHASE_TWO_STATUSES.flatMap((status) => lanes[status].map((item, index) => ({
      ...item,
      status,
      sortOrder: index + 1,
    })));
  };

  const persistStoryOrder = async (nextItems) => {
    if (!nextItems || !beginAction("reorder")) return;
    const previousItems = items;
    setItems(nextItems);
    setError("");
    try {
      const payload = await requestJson("/backend/sow/phase-two/reorder", {
        method: "PATCH",
        body: JSON.stringify({
          items: nextItems.map((item) => ({
            caseId: item.id,
            status: item.status,
            sortOrder: item.sortOrder,
          })),
        }),
      });
      const savedItems = phaseTwoRowsFromPayload(payload);
      if (mountedRef.current && savedItems.length) setItems(savedItems);
    } catch (requestError) {
      setItems(previousItems);
      setError(requestError.message);
    } finally {
      finishAction();
    }
  };

  const finishDragging = () => {
    setDraggedItemId("");
    setDragOverStatus("");
    setDragTargetItemId("");
  };

  const dropItemInStatus = (event, status, targetItemId = "") => {
    event.preventDefault();
    event.stopPropagation();
    const itemId = event.dataTransfer?.getData("text/plain") || draggedItemId;
    const draggedItem = items.find((item) => item.id === itemId);
    if (draggedItem?.clientApproved && status === "Defined") {
      finishDragging();
      setError("Remove client approval before moving this story back to Defined.");
      return;
    }
    const targetRect = targetItemId ? event.currentTarget.getBoundingClientRect?.() : null;
    const placeAfter = Boolean(targetRect && targetRect.height > 0 && event.clientY > targetRect.top + (targetRect.height / 2));
    const nextItems = orderedItemsAfterDrop(itemId, status, targetItemId, placeAfter);
    finishDragging();
    if (nextItems) persistStoryOrder(nextItems);
  };

  const moveSelectedItem = (direction) => {
    if (!selectedItem || hasPendingActions) return;
    const targetItem = selectedLaneItems[selectedLaneIndex + direction];
    if (!targetItem) return;
    const nextItems = orderedItemsAfterDrop(
      selectedItem.id,
      selectedItem.status,
      targetItem.id,
      direction > 0,
    );
    if (nextItems) persistStoryOrder(nextItems);
  };

  const createItem = async (event) => {
    event.preventDefault();
    if (!newTitle.trim() || !beginAction("create-item")) return;
    setError("");
    try {
      const payload = await requestJson("/backend/sow/phase-two", {
        method: "POST",
        body: JSON.stringify({
          title: newTitle.trim(),
          subject: newTitle.trim(),
          description: newDescription,
          priority: newPriority,
        }),
      });
      const createdItem = phaseTwoItemFromPayload(payload);
      if (!mountedRef.current) return;
      setNewTitle("");
      setNewDescription("");
      setNewPriority("Medium");
      setCreating(false);
      setDetailOpen(true);
      setEditing(false);
      setCommenting(false);
      if (createdItem) {
        setItems((current) => [...current, createdItem]);
        setSelectedItemId(createdItem.id);
      } else {
        await loadItems("", true);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      finishAction();
    }
  };

  const addComment = async (event) => {
    event.preventDefault();
    if (!selectedItem || !stripProjectRichText(comment).trim()) return;
    if (!beginAction("add-comment", selectedItem.id)) return;
    const revision = detailRevisionRef.current;
    const selectedMentions = mentionsFromRichText(comment, commentMentions);
    setError("");
    try {
      const payload = await requestJson(`/backend/sow/phase-two/${encodeURIComponent(selectedItem.id)}/comments`, {
        method: "POST",
        body: JSON.stringify({
          comment,
          mentions: selectedMentions.map((member) => member.id),
        }),
      });
      const commentData = payload?.data?.comment || payload?.comment;
      if (!mountedRef.current) return;
      if (commentData) {
        const createdComment = normalizePhaseTwoComment(commentData);
        setItems((current) => current.map((item) => item.id === selectedItem.id
          ? {
            ...item,
            comments: [...item.comments, createdComment],
            commentCount: item.commentCount + 1,
          }
          : item));
      }
      if (revision === detailRevisionRef.current) {
        setComment("");
        setCommentMentions([]);
        setCommenting(false);
      }
      if (!commentData) await loadItems("", true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      finishAction(selectedItem.id);
    }
  };

  const uploadFiles = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedItem || !selectedFiles.length) return;
    const oversizedFile = selectedFiles.find((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFile) {
      setError(`${oversizedFile.name} exceeds the 10 MB attachment limit.`);
      return;
    }
    if (!beginAction("upload-file", selectedItem.id)) return;
    setError("");
    try {
      let mergedFiles = 0;
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file, file.name);
        const payload = await requestJson(`/backend/sow/phase-two/${encodeURIComponent(selectedItem.id)}/files`, {
          method: "POST",
          body: formData,
        });
        const fileData = payload?.data?.file || payload?.file;
        if (!mountedRef.current) return;
        if (fileData) {
          const uploadedFile = normalizePhaseTwoFile(fileData);
          mergedFiles += 1;
          setItems((current) => current.map((item) => item.id === selectedItem.id
            ? {
              ...item,
              files: [...item.files, uploadedFile],
              attachmentCount: item.attachmentCount + 1,
            }
            : item));
        }
      }
      if (mergedFiles !== selectedFiles.length) await loadItems("", true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      finishAction(selectedItem.id);
    }
  };

  const downloadFile = async (file) => {
    if (!selectedItem || !file.id) return;
    setDownloadingFileId(file.id);
    setError("");
    try {
      const response = await fetch(
        `${getBackendBaseUrl()}/backend/sow/phase-two/${encodeURIComponent(selectedItem.id)}/files/${encodeURIComponent(file.id)}/download`,
        { cache: "no-store", credentials: "include", headers: { "X-SOW-Project": "riomax" } },
      );
      if (!response.ok) {
        if (response.status === 401 && mountedRef.current) window.dispatchEvent(new CustomEvent("sow:session-expired"));
        const payload = await readPayload(response);
        throw new Error(payload?.message || "The attachment could not be downloaded.");
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileNameFromDisposition(response.headers.get("content-disposition"), file.name);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setDownloadingFileId("");
    }
  };

  const busyLabel = {
    "save-item": "Saving story",
    reorder: "Saving order",
    "create-item": "Creating story",
    "add-comment": "Adding comment",
    "upload-file": "Uploading files",
  }[busyAction] || (busyAction.startsWith("workflow-") ? "Updating story" : "");
  const reorderEnabled = !hasPendingActions && !loading && !filterText.trim() && statusFilter === "All";

  return (
    <section ref={workspaceRef} className="sow-phase-two-workspace" role={embedded ? "region" : "dialog"} aria-modal={embedded ? undefined : true} aria-labelledby="sow-phase-two-title">
      <header className="sow-phase-two-header">
        <div className="sow-phase-two-header__identity">
          <button className="sow-phase-two-icon-button" type="button" onClick={onClose} aria-label="Back to Riomax engagement workspace" title="Back to engagement workspace">
            <FiArrowLeft aria-hidden="true" />
          </button>
          {!embedded && <div className="sow-phase-two-brand-pair" aria-label="Antrazal and Riomax 360">
            <BrandWordmark alt="Antrazal" className="sow-phase-two-brand-lockup is-antrazal" fallback="Antrazal" src={antrazalWordmark} />
            <span aria-hidden="true" />
            <ProjectWordmark className="sow-phase-two-brand-lockup is-riomax" projectSlug="riomax" />
          </div>}
          <div className="sow-phase-two-header__title">
            <h1 id="sow-phase-two-title">Phase 2</h1>
            <span>Scope planning</span>
          </div>
        </div>
        <div className="sow-phase-two-header__actions">
          <div className="sow-phase-two-view-toggle" role="group" aria-label="Phase 2 view">
            <button
              type="button"
              aria-label="List view"
              aria-pressed={viewMode === "list" && !detailOpen}
              onClick={showListView}
              title="List view"
            >
              <FiList aria-hidden="true" /><span>List</span>
            </button>
            <button
              type="button"
              aria-label="Split view"
              aria-pressed={viewMode === "split"}
              onClick={showSplitView}
              title="Split view"
              disabled={!items.length}
            >
              <FiColumns aria-hidden="true" /><span>Split</span>
            </button>
          </div>
          <span className="sow-phase-two-sync-state" role="status" aria-live="polite">
            {(loading || busyLabel) && <FiRefreshCw className="sow-spin" aria-hidden="true" />}
            {busyLabel || (loading ? "Loading" : `${items.length} ${items.length === 1 ? "story" : "stories"}`)}
          </span>
          <button type="button" className="sow-phase-two-icon-button" onClick={() => loadItems(selectedItemId)} disabled={loading || hasPendingActions} aria-label="Refresh Phase 2 stories" title="Refresh stories">
            <FiRefreshCw className={loading ? "sow-spin" : ""} aria-hidden="true" />
          </button>
          <button ref={createButtonRef} type="button" className="is-primary" onClick={openCreate} disabled={loading || hasPendingActions} aria-label="Add story" title="Add story">
            <FiPlus aria-hidden="true" /> <span>Add story</span>
          </button>
        </div>
      </header>

      {error && <div className="sow-phase-two-error" role="alert"><FiShield aria-hidden="true" />{error}</div>}

      <div className={`sow-phase-two-layout${detailOpen ? " is-detail-open" : ""}${viewMode === "split" ? " is-split-view" : ""}`}>
        <aside className="sow-phase-two-rail" aria-label="Phase 2 stories">
          <div className="sow-phase-two-plan-surface">
            <div className="sow-phase-two-plan-toolbar">
              <div className="sow-phase-two-search">
                <FiSearch aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  aria-label="Search Phase 2 stories"
                  type="search"
                  value={filterText}
                  onChange={(event) => setFilterText(event.target.value)}
                  placeholder="Search by story, key, priority, or assignee"
                />
              </div>
              <div className="sow-phase-two-filters" role="group" aria-label="Filter stories by status">
                <button type="button" aria-pressed={statusFilter === "All"} onClick={() => setStatusFilter("All")}>All <span>{searchedItems.length}</span></button>
                {PHASE_TWO_STATUSES.map((status) => (
                  <button type="button" key={status} aria-pressed={statusFilter === status} onClick={() => setStatusFilter(status)}>
                    {status}<span>{statusCounts[status]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="sow-phase-two-list-head" aria-hidden="true">
              <span>Story</span>
              <span>Priority</span>
              <span>Assignee</span>
              <span>Client approval</span>
              <span>Activity</span>
              <span />
              <span />
            </div>
            <div className="sow-phase-two-items">
            {loading && !items.length ? (
              <div className="sow-phase-two-empty"><FiRefreshCw className="sow-spin" aria-hidden="true" />Loading items</div>
            ) : !visibleItems.length ? (
              <div className="sow-phase-two-empty"><FiInbox aria-hidden="true" /><strong>{items.length ? "No matching stories" : "No Phase 2 stories yet"}</strong><span>{items.length ? "Clear the search to view the full list." : "Create the first story to begin planning."}</span></div>
            ) : visibleStatuses.map((status) => (
              <section
                className={`sow-phase-two-lane${dragOverStatus === status ? " is-drag-over" : ""}`}
                key={status}
                aria-label={`${status} stories`}
                onDragEnter={() => {
                  if (reorderEnabled) {
                    setDragOverStatus(status);
                    setDragTargetItemId("");
                  }
                }}
                onDragOver={(event) => {
                  if (!reorderEnabled) return;
                  event.preventDefault();
                  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                  setDragOverStatus(status);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setDragOverStatus("");
                    setDragTargetItemId("");
                  }
                }}
                onDrop={(event) => {
                  if (reorderEnabled) dropItemInStatus(event, status);
                }}
              >
                <header>
                  <strong>{status}</strong>
                  <span>{visibleItemsByStatus[status].length}</span>
                </header>
                <div className="sow-phase-two-lane__items">
                  {visibleItemsByStatus[status].length ? visibleItemsByStatus[status].map((item) => (
                    <article
                      key={item.id}
                      className={`${item.id === selectedItem?.id ? "is-selected" : ""}${item.id === draggedItemId ? " is-dragging" : ""}${item.id === dragTargetItemId ? " is-drop-target" : ""}`}
                      onDragEnter={(event) => {
                        if (!reorderEnabled) return;
                        event.preventDefault();
                        setDragOverStatus(status);
                        setDragTargetItemId(item.id);
                      }}
                      onDragOver={(event) => {
                        if (!reorderEnabled) return;
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                        setDragOverStatus(status);
                        setDragTargetItemId(item.id);
                      }}
                      onDrop={(event) => {
                        if (reorderEnabled) dropItemInStatus(event, status, item.id);
                      }}
                    >
                      <button
                        type="button"
                        className="sow-phase-two-item__select"
                        aria-busy={Boolean(pendingActions[item.id])}
                        data-phase-two-item-id={item.id}
                        aria-label={item.title}
                        aria-describedby={`sow-phase-two-item-meta-${item.id}`}
                        aria-current={item.id === selectedItem?.id ? "true" : undefined}
                        onClick={() => selectItem(item.id)}
                        onKeyDown={(event) => {
                          if (event.key === "ArrowDown") selectAdjacentItem(event, item.id, 1);
                          if (event.key === "ArrowUp") selectAdjacentItem(event, item.id, -1);
                        }}
                      >
                        <span className="sow-phase-two-item__story">
                          <small>{item.key ? `P2-${item.key}` : "P2"}</small>
                          <strong>{item.title}</strong>
                        </span>
                        <span className={`sow-phase-two-priority is-${item.priority.toLowerCase()}`}><FiFlag aria-hidden="true" />{item.priority}</span>
                        <span className={`sow-phase-two-item__assignee${item.assignee ? "" : " is-unassigned"}`}>
                          <FiUser aria-hidden="true" />{item.assignee?.name || "Unassigned"}
                        </span>
                        <span className={`sow-phase-two-item__approval${item.clientApproved ? " is-approved" : ""}`}>
                          {item.clientApproved ? <FiCheckCircle aria-hidden="true" /> : <FiClock aria-hidden="true" />}
                          {item.clientApproved ? "Approved" : "Pending"}
                        </span>
                        <span className="sow-phase-two-item__activity">
                          <span aria-label={`${item.commentCount} comments`}><FiMessageSquare aria-hidden="true" />{item.commentCount}</span>
                          <span aria-label={`${item.attachmentCount} attachments`}><FiPaperclip aria-hidden="true" />{item.attachmentCount}</span>
                        </span>
                        {pendingActions[item.id]
                          ? <FiRefreshCw className="sow-phase-two-item__open sow-spin" aria-label="Saving story" />
                          : <FiChevronRight className="sow-phase-two-item__open" aria-hidden="true" />}
                        <span className="sow-phase-two-sr-only" id={`sow-phase-two-item-meta-${item.id}`}>
                          {`${item.status}. ${item.priority} priority. ${item.assignee?.name || "Unassigned"}. Client approval ${item.clientApproved ? "approved" : "pending"}. ${item.commentCount} comments. ${item.attachmentCount} attachments.`}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="sow-phase-two-item__drag"
                        draggable={reorderEnabled}
                        disabled={!reorderEnabled}
                        aria-label={`Reorder ${item.title}`}
                        title={reorderEnabled ? "Drag to reorder" : "Clear search and show all statuses to reorder"}
                        onDragStart={(event) => startDraggingItem(event, item.id)}
                        onDragEnd={finishDragging}
                      >
                        <FiMove aria-hidden="true" />
                      </button>
                    </article>
                  )) : <p>Drop a story here</p>}
                </div>
              </section>
            ))}
            </div>
          </div>
        </aside>

        <main className="sow-phase-two-detail">
          {selectedItem ? (
            <>
              <header className="sow-phase-two-detail__header">
                <button className="sow-phase-two-detail__back" type="button" onClick={closeDetail} aria-label="Back to list" title="Back to list">
                  <FiArrowLeft aria-hidden="true" /> <span>Back to list</span>
                </button>
                <div className="sow-phase-two-detail__title">
                  <small>{selectedItem.key ? `Phase 2 / P2-${selectedItem.key}` : "Phase 2 item"}</small>
                  <h2 ref={detailTitleRef} tabIndex="-1">{selectedItem.title}</h2>
                  <div className="sow-phase-two-detail__meta">
                    <span className="sow-phase-two-detail__status">{selectedItem.status}</span>
                    <span>Created by {selectedItem.createdBy}</span>
                    <span>Updated {formatCaseDate(selectedItem.updatedAt) || "recently"}</span>
                  </div>
                </div>
                <div className="sow-phase-two-detail__actions">
                  {!editing && (
                    <button className="sow-phase-two-detail__edit" type="button" onClick={startEditing} disabled={Boolean(busyAction)}>
                      <FiEdit2 aria-hidden="true" /> Edit story
                    </button>
                  )}
                  <button className="sow-phase-two-detail__rank" type="button" onClick={() => moveSelectedItem(-1)} disabled={hasPendingActions || selectedLaneIndex <= 0} aria-label="Move story up" title="Move story up">
                    <FiChevronUp aria-hidden="true" />
                  </button>
                  <button className="sow-phase-two-detail__rank" type="button" onClick={() => moveSelectedItem(1)} disabled={hasPendingActions || selectedLaneIndex < 0 || selectedLaneIndex >= selectedLaneItems.length - 1} aria-label="Move story down" title="Move story down">
                    <FiChevronDown aria-hidden="true" />
                  </button>
                </div>
              </header>

              <div className="sow-phase-two-detail__scroll">
                <div className="sow-phase-two-story-grid">
                  <div className="sow-phase-two-story-main">
                    <section className="sow-phase-two-section sow-phase-two-description" aria-labelledby="sow-phase-two-description-title">
                      <header>
                        <h3 id="sow-phase-two-description-title">Description</h3>
                      </header>
                      {editing ? (
                        <form className="sow-phase-two-edit-form" onSubmit={saveItem}>
                          <label htmlFor="sow-phase-two-edit-title">Title</label>
                          <input
                            id="sow-phase-two-edit-title"
                            value={editTitle}
                            onChange={(event) => setEditTitle(event.target.value)}
                            maxLength={160}
                            required
                            disabled={busyAction === "save-item"}
                          />
                          <label>Description</label>
                          <RichTextEditor
                            ariaLabel="Phase 2 item description"
                            minHeight={220}
                            value={editDescription}
                            onChange={setEditDescription}
                            placeholder="Define the outcome, business context, scope, dependencies, acceptance criteria, and open decisions."
                            disabled={busyAction === "save-item"}
                          />
                          <div className="sow-phase-two-form-actions">
                            <button type="button" className="is-secondary" onClick={() => setEditing(false)} disabled={busyAction === "save-item"}>Cancel</button>
                            <button type="submit" className="is-primary" disabled={Boolean(busyAction) || !editTitle.trim()}>
                              {busyAction === "save-item" ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiSave aria-hidden="true" />}
                              {busyAction === "save-item" ? "Saving" : "Save changes"}
                            </button>
                          </div>
                        </form>
                      ) : (
                        <ProjectRichText className="sow-phase-two-rich-text" value={selectedItem.description} emptyText="No description has been added. Use Edit to document this Phase 2 scope." />
                      )}
                    </section>

                    <section className="sow-phase-two-section sow-phase-two-files" aria-labelledby="sow-phase-two-files-title">
                      <header>
                        <div><h3 id="sow-phase-two-files-title">Attachments</h3><span>{selectedItem.files.length}</span></div>
                        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={Boolean(busyAction)}>
                          {busyAction === "upload-file" ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiUploadCloud aria-hidden="true" />}
                          {busyAction === "upload-file" ? "Uploading" : "Add files"}
                        </button>
                        <input ref={fileInputRef} type="file" multiple hidden onChange={uploadFiles} />
                      </header>
                      {selectedItem.files.length ? (
                        <div className="sow-phase-two-file-list">
                          {selectedItem.files.map((file) => (
                            <article key={file.id || file.name}>
                              <span><FiFile aria-hidden="true" /></span>
                              <div><strong>{file.name}</strong><small>{[formatFileSize(file.size), file.uploadedBy, formatCaseDate(file.createdAt)].filter(Boolean).join(" · ") || "Project attachment"}</small></div>
                              <button type="button" onClick={() => downloadFile(file)} disabled={!file.id || downloadingFileId === file.id} aria-label={`Download ${file.name}`}>
                                {downloadingFileId === file.id ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
                              </button>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="sow-phase-two-section__empty"><FiPaperclip aria-hidden="true" /><span>No attachments yet.</span></div>
                      )}
                    </section>

                    <section className="sow-phase-two-section sow-phase-two-discussion" aria-labelledby="sow-phase-two-comments-title">
                      <header>
                        <div><h3 id="sow-phase-two-comments-title">Discussion</h3><span>{selectedItem.comments.length}</span></div>
                        {!commenting && (
                          <button type="button" onClick={() => setCommenting(true)} disabled={Boolean(busyAction)}>
                            <FiPlus aria-hidden="true" /> Add comment
                          </button>
                        )}
                      </header>
                      {selectedItem.comments.length ? (
                        <div className="sow-phase-two-comment-list">
                          {selectedItem.comments.map((entry) => (
                            <article key={entry.id} data-phase-two-comment={entry.id}>
                              <span className="sow-phase-two-avatar">{getInitials(entry.author)}</span>
                              <div>
                                <header><strong>{entry.author}</strong><time>{formatCaseDate(entry.createdAt)}</time></header>
                                <ProjectCommentBody value={entry.body} mentions={entry.mentions} />
                              </div>
                            </article>
                          ))}
                        </div>
                      ) : (
                        <div className="sow-phase-two-section__empty"><FiMessageSquare aria-hidden="true" /><span>No activity yet.</span></div>
                      )}
                      {commenting && <form className="sow-phase-two-comment-form" onSubmit={addComment}>
                        <div className="sow-phase-two-comment-form__identity"><span>{getInitials(currentUser?.name)}</span><strong>{currentUser?.name}</strong></div>
                        <RichTextEditor
                          ariaLabel="Add a Phase 2 comment"
                          assignees={members}
                          mentions={commentMentions}
                          minHeight={130}
                          value={comment}
                          onChange={setComment}
                          onMentionsChange={setCommentMentions}
                          placeholder="Add a comment..."
                          disabled={busyAction === "add-comment"}
                        />
                        <div className="sow-phase-two-form-actions">
                          <button type="button" className="is-secondary" onClick={() => { setComment(""); setCommentMentions([]); setCommenting(false); }} disabled={busyAction === "add-comment"}>Cancel</button>
                          <button type="submit" className="is-primary" disabled={Boolean(busyAction) || !stripProjectRichText(comment).trim()}>
                            {busyAction === "add-comment" ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiSend aria-hidden="true" />}
                            {busyAction === "add-comment" ? "Adding comment" : "Add comment"}
                          </button>
                        </div>
                      </form>}
                    </section>
                  </div>

                  <aside className="sow-phase-two-story-sidebar" aria-label="Story details">
                    <h3>Details</h3>
                    <div className="sow-phase-two-workflow" aria-label="Story workflow">
                      <label>
                        <span>Status</span>
                        <select
                          aria-label="Story status"
                          value={selectedItem.status}
                          onChange={(event) => updateWorkflow(selectedItem.id, { status: event.target.value })}
                          disabled={Boolean(busyAction)}
                        >
                          {PHASE_TWO_STATUSES.map((status) => (
                            <option key={status} value={status} disabled={selectedItem.clientApproved && status === "Defined"}>{status}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Priority</span>
                        <select
                          aria-label="Story priority"
                          value={selectedItem.priority}
                          onChange={(event) => updateWorkflow(selectedItem.id, { priority: event.target.value })}
                          disabled={Boolean(busyAction)}
                        >
                          {PHASE_TWO_PRIORITIES.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                        </select>
                      </label>
                      <label className="sow-phase-two-workflow__assignee">
                        <span>Assignee</span>
                        <select
                          aria-label="Story assignee"
                          value={selectedItem.assignee?.id || ""}
                          onChange={(event) => updateWorkflow(selectedItem.id, { assigneeId: event.target.value || null })}
                          disabled={Boolean(busyAction)}
                        >
                          <option value="">Unassigned</option>
                          {!selectedAssigneeIsEligible && selectedItem.assignee && (
                            <option value={selectedItem.assignee.id} disabled>{selectedItem.assignee.name} (access removed)</option>
                          )}
                          {assignees.map((assignee) => <option key={assignee.id} value={assignee.id}>{assignee.name}</option>)}
                        </select>
                      </label>
                      <div className="sow-phase-two-workflow__approval">
                        <span>Client approval</span>
                        <button
                          type="button"
                          role="switch"
                          aria-label="Client approval"
                          aria-checked={selectedItem.clientApproved}
                          className={selectedItem.clientApproved ? "is-approved" : ""}
                          onClick={() => updateWorkflow(selectedItem.id, { clientApproved: !selectedItem.clientApproved })}
                          disabled={Boolean(busyAction) || !currentUser?.canSign}
                          title={currentUser?.canSign ? "Change client approval" : "Only an authorised client approver can change this"}
                        >
                          <FiCheckCircle aria-hidden="true" />
                          {selectedItem.clientApproved ? "Approved" : "Not approved"}
                        </button>
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            </>
          ) : (
            <div className="sow-phase-two-detail__empty"><FiBriefcase aria-hidden="true" /><strong>No Phase 2 story selected</strong><span>Select a story or create a new one.</span></div>
          )}
        </main>
      </div>

      {creating && (
        <div className="sow-phase-two-create-backdrop" role="presentation">
          <section ref={createDialogRef} className="sow-phase-two-create" role="dialog" aria-modal="true" aria-labelledby="sow-phase-two-create-title">
            <header><div><h2 id="sow-phase-two-create-title">New Phase 2 story</h2><p>Capture one proposed capability or outcome.</p></div><button type="button" onClick={closeCreate} disabled={busyAction === "create-item"} aria-label="Close new Phase 2 story"><FiX aria-hidden="true" /></button></header>
            <form onSubmit={createItem}>
              <label htmlFor="sow-phase-two-new-title">Title</label>
              <input id="sow-phase-two-new-title" autoFocus value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="A concise outcome or capability" maxLength={160} required disabled={busyAction === "create-item"} />
              <label htmlFor="sow-phase-two-new-priority">Priority</label>
              <select id="sow-phase-two-new-priority" value={newPriority} onChange={(event) => setNewPriority(event.target.value)} disabled={busyAction === "create-item"}>
                {PHASE_TWO_PRIORITIES.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </select>
              <label>Description</label>
              <RichTextEditor
                ariaLabel="New Phase 2 story description"
                minHeight={260}
                value={newDescription}
                onChange={setNewDescription}
                placeholder="Document the proposed outcome, context, scope, dependencies, acceptance criteria, and open questions."
                disabled={busyAction === "create-item"}
              />
              <div className="sow-phase-two-form-actions">
                <button type="button" className="is-secondary" onClick={closeCreate} disabled={busyAction === "create-item"}>Cancel</button>
                <button type="submit" className="is-primary" disabled={busyAction === "create-item" || !newTitle.trim()}>
                  {busyAction === "create-item" ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                  {busyAction === "create-item" ? "Creating" : "Create story"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}

function StoryLinks() {
  return (
    <aside className="sow-auth-stories" aria-labelledby="sow-stories-title">
      <div className="sow-stories-heading">
        <span>Selected work</span>
        <h2 id="sow-stories-title">Built for work that has to hold up</h2>
      </div>
      <div className="sow-story-list">
        {STORIES.map((story) => (
          <a
            className="sow-story-link"
            href={story.url}
            key={story.url}
            target="_blank"
            rel="noreferrer"
          >
            <img src={story.image} alt="" />
            <span>
              <small>{story.customer}</small>
              <strong>{story.title}</strong>
            </span>
            <FiExternalLink aria-hidden="true" />
          </a>
        ))}
      </div>
    </aside>
  );
}

function LoadingState() {
  return (
    <main className="sow-loading-shell" aria-live="polite">
      <div className="sow-loading-mark"><FiShield aria-hidden="true" /></div>
      <p>Checking secure access</p>
    </main>
  );
}

export default function SowPortal({ requestedProjectSlug = "jacobs" }) {
  const planStudioRef = useRef(null);
  const sessionRevokedRef = useRef(false);
  const recoveryActiveRef = useRef(false);
  const sessionOwnerRef = useRef("");
  const [planRecovery, setPlanRecovery] = useState(null);
  const requestJson = useCallback((path, options = {}) => {
    if (sessionRevokedRef.current && path !== "/backend/sow/auth/logout"
      && (recoveryActiveRef.current || !path.startsWith("/backend/sow/auth/"))) {
      return Promise.reject(Object.assign(new Error("Your secure session ended."), { status: 401 }));
    }
    const epoch = sessionEpochRef.current;
    return requestWorkspaceJson(path, {
      ...options,
      projectSlug: requestedProjectSlug,
      isSessionCurrent: () => epoch === sessionEpochRef.current,
    });
  }, [requestedProjectSlug]);
  const portalConfig = SOW_PROJECT_CONFIG[requestedProjectSlug] || SOW_PROJECT_CONFIG.jacobs;
  const [screen, setScreen] = useState("checking");
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState("");
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendAt, setResendAt] = useState(0);
  const [secondsUntilResend, setSecondsUntilResend] = useState(0);
  const [documentLoading, setDocumentLoading] = useState(true);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportTab, setSupportTab] = useState("ask");
  const [caseSubject, setCaseSubject] = useState("");
  const [caseQuestion, setCaseQuestion] = useState("");
  const [supportCases, setSupportCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [caseBusy, setCaseBusy] = useState(false);
  const [supportError, setSupportError] = useState("");
  const [expandedCaseId, setExpandedCaseId] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [caseWorkspaceOpen, setCaseWorkspaceOpen] = useState(false);
  const [phaseTwoWorkspaceOpen, setPhaseTwoWorkspaceOpen] = useState(false);
  const [activeApp, setActiveApp] = useState(workspaceAppFromUrl);
  const [notificationTarget, setNotificationTarget] = useState(workspaceTargetFromUrl);
  const [legacyPhaseTwoOpened, setLegacyPhaseTwoOpened] = useState(() => workspaceTargetFromUrl()?.kind === 'case');
  const [visitedApps, setVisitedApps] = useState(() => new Set([workspaceAppFromUrl()]));
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [caseComment, setCaseComment] = useState("");
  const [commentPendingIds, setCommentPendingIds] = useState([]);
  const profileMenuRef = useRef(null);
  const documentFrameRef = useRef(null);
  const caseLoadRef = useRef({ id: 0, controller: null });
  const caseCommentRequestsRef = useRef(new Set());
  const selectedCaseIdRef = useRef(selectedCaseId);
  selectedCaseIdRef.current = selectedCaseId;
  const authBusyRef = useRef(false);
  const caseCreateBusyRef = useRef(false);
  const sessionEpochRef = useRef(0);

  const selectWorkspaceApp = useCallback((app, updateHistory = true) => {
    if (sessionRevokedRef.current) return;
    if (app !== "home" && !DEAL_DESK_APPS.some(item => item.id === app)) return;
    setActiveApp(app);
    setNotificationTarget(null);
    setVisitedApps(current => new Set([...current, app]));
    setProfileOpen(false); setSupportOpen(false); setCaseWorkspaceOpen(false);
    if (app === "ideas") setPhaseTwoWorkspaceOpen(true);
    if (updateHistory) {
      const url = new URL(window.location.href);
      ['ticket', 'idea', 'case'].forEach(key => url.searchParams.delete(key));
      if ((url.searchParams.get("app") || "home") !== app || url.href !== window.location.href) {
        if (app === "home") url.searchParams.delete("app");
        else url.searchParams.set("app", app);
        window.history.pushState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    }
  }, []);

  useEffect(() => {
    const syncApp = () => {
      selectWorkspaceApp(workspaceAppFromUrl(), false);
      const target = workspaceTargetFromUrl();
      if (target) { setNotificationTarget({ ...target, requestId: `${target.requestId}:${Date.now()}` }); if (target.kind === 'case') setLegacyPhaseTwoOpened(true); }
    };
    window.addEventListener("popstate", syncApp);
    return () => window.removeEventListener("popstate", syncApp);
  }, [selectWorkspaceApp]);

  useEffect(() => {
    if (screen === "workspace" && activeApp === "ideas") setPhaseTwoWorkspaceOpen(true);
  }, [screen, activeApp]);

  const capturePlanRecovery = useCallback(async () => {
    const epoch = sessionEpochRef.current;
    setPlanRecovery({ phase: "capturing" });
    try {
      const json = await planStudioRef.current?.recover();
      if (epoch !== sessionEpochRef.current || !recoveryActiveRef.current) return;
      setPlanRecovery(json ? { phase: "ready", json } : null);
      recoveryActiveRef.current = !!json;
      setPhaseTwoWorkspaceOpen(false);
      setSession(null);
      setScreen(json ? "recovery" : "email");
    } catch (failure) {
      if (epoch === sessionEpochRef.current && recoveryActiveRef.current) {
        setPlanRecovery({ phase: "failed", error: failure.message });
      }
    }
  }, []);

  const expireSession = useCallback(({ discardDraft = false } = {}) => {
    if (sessionRevokedRef.current && !discardDraft) return;
    sessionRevokedRef.current = true;
    sessionEpochRef.current += 1;
    const recoverPlan = !discardDraft && !!planStudioRef.current;
    recoveryActiveRef.current = recoverPlan;
    if (discardDraft) { planStudioRef.current?.discard(); setPlanRecovery(null); }
    caseLoadRef.current.id += 1;
    caseLoadRef.current.controller?.abort();
    caseCommentRequestsRef.current.clear();
    setCommentPendingIds([]);
    setCasesLoading(false);
    // Keep only the old frame alive until it acknowledges a complete local snapshot.
    if (!recoverPlan) setSession(null);
    setDocumentLoading(true);
    setDocumentModalOpen(false);
    setSupportOpen(false);
    setProfileOpen(false);
    setCaseWorkspaceOpen(false);
    setPhaseTwoWorkspaceOpen(recoverPlan);
    setActiveApp(workspaceAppFromUrl());
    setVisitedApps(new Set(recoverPlan ? [] : [workspaceAppFromUrl()]));
    setSupportCases([]);
    setSelectedCaseId("");
    setCaseComment("");
    setScreen(recoverPlan ? "recovering" : "email");
    setError("Your secure session ended. Request a new verification code to continue.");
    if (recoverPlan) capturePlanRecovery();
  }, [capturePlanRecovery]);

  const clearPlanRecovery = useCallback(() => {
    expireSession({ discardDraft: true });
  }, [expireSession]);

  const maskedEmail = useMemo(() => {
    const [name, domain] = email.split("@");
    if (!name || !domain) return email;
    return `${name.slice(0, 2)}${"•".repeat(Math.max(2, Math.min(6, name.length - 2)))}@${domain}`;
  }, [email]);

  useEffect(() => {
    window.addEventListener("sow:session-expired", expireSession);
    return () => window.removeEventListener("sow:session-expired", expireSession);
  }, [expireSession]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const epoch = ++sessionEpochRef.current;
    planStudioRef.current?.discard();
    recoveryActiveRef.current = false;
    sessionRevokedRef.current = false;
    sessionOwnerRef.current = "";
    setPlanRecovery(null);
    setScreen("checking");
    setSession(null);
    setLegacyPhaseTwoOpened(false);
    setActiveApp(workspaceAppFromUrl());
    setVisitedApps(new Set([workspaceAppFromUrl()]));
    setDocumentModalOpen(false);
    setDocumentLoading(true);
    setSupportOpen(false);
    setProfileOpen(false);
    setCaseWorkspaceOpen(false);
    setPhaseTwoWorkspaceOpen(false);
    setSupportCases([]);
    setSelectedCaseId("");
    setCaseComment("");
    setCaseSubject("");
    setCaseQuestion("");
    setSupportError("");
    caseCommentRequestsRef.current.clear();
    setCommentPendingIds([]);
    const restoreSession = async () => {
      try {
        const payload = await requestJson("/backend/sow/session", { signal: controller.signal });
        if (!active || epoch !== sessionEpochRef.current) return;
        if (payload?.data?.project?.slug !== requestedProjectSlug) {
          try {
            await requestJson("/backend/sow/auth/logout", {
              method: "POST",
              body: JSON.stringify({}),
            });
          } catch {
            // A stale project session must not be reused on another project route.
          }
          if (!active) return;
          expireSession();
          return;
        }
        sessionOwnerRef.current = `${payload.data.project.id}:${payload.data.user?.email || ""}`;
        setSession(payload?.data || null);
        setScreen("workspace");
      } catch (requestError) {
        if (!active || epoch !== sessionEpochRef.current || requestError.name === "AbortError") return;
        setScreen("email");
        if (requestError.status !== 401) {
          setError(requestError.message);
        }
      }
    };
    restoreSession();
    return () => {
      active = false;
      sessionEpochRef.current += 1;
      controller.abort();
      caseLoadRef.current.id += 1;
      caseLoadRef.current.controller?.abort();
    };
  }, [expireSession, requestedProjectSlug, requestJson]);

  useEffect(() => {
    if (!resendAt) {
      setSecondsUntilResend(0);
      return undefined;
    }
    const updateCountdown = () => {
      setSecondsUntilResend(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    };
    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendAt]);

  useEffect(() => {
    if (screen !== "workspace") return undefined;
    let active = true;
    let checking = false;
    const controller = new AbortController();
    const verifyActiveSession = async () => {
      if (checking || document.visibilityState === "hidden") return;
      checking = true;
      const epoch = sessionEpochRef.current;
      try {
        const payload = await requestJson("/backend/sow/session", { signal: controller.signal });
        if (!active || epoch !== sessionEpochRef.current) return;
        const owner = `${payload?.data?.project?.id}:${payload?.data?.user?.email || ""}`;
        if (payload?.data?.project?.slug !== requestedProjectSlug || owner !== sessionOwnerRef.current) {
          expireSession({ discardDraft: true });
          setError("Your secure session changed. Sign in again for this workspace.");
          return;
        }
        setSession(payload?.data || null);
      } catch (requestError) {
        if (active && epoch === sessionEpochRef.current && requestError.status === 401) {
          expireSession();
        }
      } finally {
        checking = false;
      }
    };
    const intervalId = window.setInterval(verifyActiveSession, 5 * 60 * 1000);
    window.addEventListener("focus", verifyActiveSession);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(intervalId);
      window.removeEventListener("focus", verifyActiveSession);
    };
  }, [expireSession, requestedProjectSlug, screen, requestJson]);

  useEffect(() => {
    if (!profileOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setProfileOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [profileOpen]);

  useEffect(() => {
    if (screen !== "workspace" || session?.project?.slug !== "riomax" || activeApp !== "sow") return undefined;
    const focusDocumentSearch = (event) => {
      const isSearchShortcut =
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        (event.code === "KeyK" || String(event.key).toLowerCase() === "k");
      if (!isSearchShortcut) return;
      event.preventDefault();
      event.stopPropagation();
      documentFrameRef.current?.contentWindow?.postMessage(
        { type: "sow:focus-document-search" },
        "*",
      );
      documentFrameRef.current?.focus();
    };
    window.addEventListener("keydown", focusDocumentSearch, true);
    return () => window.removeEventListener("keydown", focusDocumentSearch, true);
  }, [screen, session?.project?.slug, activeApp]);

  useEffect(() => {
    if (screen !== "workspace" || !SOW_PROJECT_CONFIG[session?.project?.slug]) return undefined;
    const handleDocumentRequest = (event) => {
      if (sessionRevokedRef.current) return;
      if (event.source !== documentFrameRef.current?.contentWindow) return;
      const request = event.data;
      if (request?.type === "sow:document-modal-state" && typeof request.open === "boolean") {
        setDocumentModalOpen(request.open);
        if (request.open) {
          setProfileOpen(false);
          setSupportOpen(false);
        }
        return;
      }
      if (request?.type === "sow:session-expired") {
        expireSession();
        return;
      }
      const projectSlug = session?.project?.slug;
      if (
        ["sow:open-phase-two-discussion", "sow:open-phase-two-workspace"].includes(request?.type) &&
        projectSlug === "riomax" &&
        session?.project?.phaseTwoEnabled
      ) {
        setProfileOpen(false);
        setSupportOpen(false);
        setCaseWorkspaceOpen(false);
        selectWorkspaceApp("ideas");
        return;
      }
      const approvalTypes = projectSlug === "jacobs"
        ? ["sow:approval-load-request", "sow:approval-save-request", "sow:approval-sign-request"]
        : [];
      const supportedTypes = [
        "sow:export-assets-request",
        "sow:asset-download-request",
        ...approvalTypes,
      ];
      if (
        !request ||
        !supportedTypes.includes(request.type)
      ) {
        return;
      }
      const requestId = typeof request.requestId === "string" ? request.requestId : "";
      const responseTypes = {
        "sow:asset-download-request": "sow:asset-download-response",
        "sow:export-assets-request": "sow:export-assets-response",
        "sow:approval-load-request": "sow:approval-load-response",
        "sow:approval-save-request": "sow:approval-save-response",
        "sow:approval-sign-request": "sow:approval-sign-response",
      };
      const responseType = responseTypes[request.type];
      const requestFrame = event.source;
      const requestEpoch = sessionEpochRef.current;
      const isCurrentDocument = () =>
        requestEpoch === sessionEpochRef.current &&
        requestFrame === documentFrameRef.current?.contentWindow;
      const currentActor = {
        name: session?.user?.name || "",
        email: session?.user?.email || "",
        canSign: session?.user?.canSign === true,
      };
      const respond = (payload, transfer = []) => {
        if (!isCurrentDocument()) return;
        requestFrame.postMessage(
          { type: responseType, requestId, ...payload },
          "*",
          transfer,
        );
      };

      if (request.type === "sow:approval-load-request") {
        if (!requestId) return;
        requestJson("/backend/sow/approvals")
          .then((payload) =>
            respond({ approval: payload?.data?.approval || { decisions: [] }, currentActor }),
          )
          .catch((requestError) =>
            respond({ error: requestError.message || "SOW approvals could not be loaded." }),
          );
        return;
      }

      if (request.type === "sow:approval-save-request") {
        if (!requestId || !Array.isArray(request.decisions)) {
          respond({ error: "The SOW approval update is invalid." });
          return;
        }
        requestJson("/backend/sow/approvals", {
          method: "PUT",
          body: JSON.stringify({ decisions: request.decisions }),
        })
          .then((payload) =>
            respond({ approval: payload?.data?.approval || { decisions: [] }, currentActor }),
          )
          .catch((requestError) =>
            respond({ error: requestError.message || "SOW approvals could not be saved." }),
          );
        return;
      }

      if (request.type === "sow:approval-sign-request") {
        if (
          !requestId ||
          !request.signature ||
          typeof request.signature !== "object" ||
          Array.isArray(request.signature)
        ) {
          respond({ error: "The SOW signature request is invalid." });
          return;
        }
        requestJson("/backend/sow/approvals/sign", {
          method: "POST",
          body: JSON.stringify({ signature: request.signature }),
        })
          .then((payload) =>
            respond({ approval: payload?.data?.approval || { decisions: [] }, currentActor }),
          )
          .catch((requestError) =>
            respond({ error: requestError.message || "The SOW could not be signed." }),
          );
        return;
      }

      const fetchProtectedAsset = async (assetId) => {
        const response = await fetch(
          `${getBackendBaseUrl()}/backend/sow/assets/${encodeURIComponent(assetId)}/download?projectSlug=${encodeURIComponent(projectSlug)}`,
          { cache: "no-store", credentials: "include" },
        );
        if (!response.ok) {
          if (response.status === 401 && isCurrentDocument()) {
            expireSession();
          }
          throw new Error("The project document could not be downloaded. Please try again.");
        }
        return { id: assetId, data: await response.arrayBuffer() };
      };

      if (request.type === "sow:asset-download-request") {
        const assetId = typeof request.assetId === "string" ? request.assetId : "";
        const metadata = portalConfig.protectedAssets[assetId];
        if (!requestId || !metadata) {
          respond({ error: "The requested project document is not available." });
          return;
        }
        fetchProtectedAsset(assetId)
          .then((asset) =>
            respond(
              {
                asset: {
                  ...asset,
                  fileName: metadata.fileName,
                  contentType: metadata.contentType,
                },
              },
              [asset.data],
            ),
          )
          .catch((requestError) =>
            respond({ error: requestError.message || "The project document could not be downloaded." }),
          );
        return;
      }

      const assetIds = Array.isArray(request.assetIds) ? request.assetIds : [];
      const exportAssetIds = new Set(Object.keys(portalConfig.protectedAssets));
      const validAssetIds = assetIds.filter(
        (assetId, index) =>
          typeof assetId === "string" &&
          exportAssetIds.has(assetId) &&
          assetIds.indexOf(assetId) === index,
      );
      if (
        !requestId ||
        validAssetIds.length !== assetIds.length ||
        validAssetIds.length > portalConfig.maximumExportAssets
      ) {
        respond({ error: "The export requested an unsupported project asset." });
        return;
      }
      Promise.all(validAssetIds.map(fetchProtectedAsset))
        .then((assets) => respond({ assets }, assets.map((asset) => asset.data)))
        .catch((requestError) => respond({ error: requestError.message || "Project assets could not be exported." }));
    };
    window.addEventListener("message", handleDocumentRequest);
    return () => window.removeEventListener("message", handleDocumentRequest);
  }, [
    screen,
    expireSession,
    session?.project?.slug,
    session?.user?.canSign,
    session?.user?.email,
    session?.user?.name,
    session?.project?.phaseTwoEnabled,
    portalConfig,
    requestJson,
    selectWorkspaceApp,
  ]);

  const requestOtp = async (event) => {
    event?.preventDefault();
    if (authBusyRef.current) return;
    authBusyRef.current = true;
    const epoch = sessionEpochRef.current;
    setBusy(true);
    setError("");
    try {
      const payload = await requestJson("/backend/sow/auth/request-otp", {
        method: "POST",
        body: JSON.stringify({ email, projectSlug: requestedProjectSlug }),
      });
      if (epoch !== sessionEpochRef.current) return;
      setChallengeId(payload?.data?.challengeId || "");
      setOtp("");
      setResendAt(Date.now() + Number(payload?.data?.resendAfter || 60) * 1000);
      setScreen("otp");
    } catch (requestError) {
      if (epoch === sessionEpochRef.current) setError(requestError.message);
    } finally {
      authBusyRef.current = false;
      setBusy(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (authBusyRef.current) return;
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the six-digit code from your email.");
      return;
    }
    authBusyRef.current = true;
    const epoch = sessionEpochRef.current;
    setBusy(true);
    setError("");
    try {
      const payload = await requestJson("/backend/sow/auth/verify-otp", {
        method: "POST",
        body: JSON.stringify({ challengeId, otp }),
      });
      if (epoch !== sessionEpochRef.current) return;
      if (payload?.data?.project?.slug !== requestedProjectSlug || !payload?.data?.user) {
        throw new Error("Your session could not be verified for this project. Request a new code.");
      }
      sessionEpochRef.current += 1;
      sessionRevokedRef.current = false;
      recoveryActiveRef.current = false;
      setPlanRecovery(null);
      sessionOwnerRef.current = `${payload.data.project.id}:${payload.data.user?.email || ""}`;
      setSession(payload?.data || null);
      setDocumentLoading(true);
      setScreen("workspace");
    } catch (requestError) {
      if (epoch === sessionEpochRef.current) setError(requestError.message);
    } finally {
      authBusyRef.current = false;
      setBusy(false);
    }
  };

  const logout = async () => {
    if (authBusyRef.current) return;
    authBusyRef.current = true;
    expireSession({ discardDraft: true });
    const epoch = sessionEpochRef.current;
    setBusy(true);
    try {
      await requestJson("/backend/sow/auth/logout", {
        method: "POST",
        body: JSON.stringify({}),
      });
    } catch {
      // The local screen is cleared even if the remote session has already expired.
    } finally {
      authBusyRef.current = false;
      if (epoch !== sessionEpochRef.current) return;
      setSession(null);
      setChallengeId("");
      setOtp("");
      setEmail("");
      setError("");
      setSupportOpen(false);
      setProfileOpen(false);
      setCaseWorkspaceOpen(false);
      setPhaseTwoWorkspaceOpen(false);
      setSupportCases([]);
      setSelectedCaseId("");
      setCaseComment("");
      setScreen("email");
      setBusy(false);
    }
  };

  const returnToEmail = () => {
    setScreen("email");
    setChallengeId("");
    setOtp("");
    setError("");
  };

  const loadCases = async (preferredCaseId = "") => {
    caseLoadRef.current.controller?.abort();
    const controller = new AbortController();
    const requestId = caseLoadRef.current.id + 1;
    const epoch = sessionEpochRef.current;
    caseLoadRef.current = { id: requestId, controller };
    setCasesLoading(true);
    setSupportError("");
    try {
      const payload = await requestJson("/backend/sow/cases", { signal: controller.signal });
      if (requestId !== caseLoadRef.current.id || epoch !== sessionEpochRef.current) return;
      const rows = Array.isArray(payload?.data?.cases) ? payload.data.cases : [];
      setSupportCases(rows);
      setExpandedCaseId((current) => {
        const nextId = preferredCaseId || current;
        return rows.some((row) => row.id === nextId) ? nextId : rows[0]?.id || "";
      });
      setSelectedCaseId((current) => {
        const nextId = preferredCaseId || current;
        return rows.some((row) => row.id === nextId) ? nextId : rows[0]?.id || "";
      });
    } catch (requestError) {
      if (requestId === caseLoadRef.current.id && epoch === sessionEpochRef.current
        && requestError.name !== "AbortError") setSupportError(requestError.message);
    } finally {
      if (requestId === caseLoadRef.current.id) setCasesLoading(false);
    }
  };

  const openSupport = (tab = "ask") => {
    setProfileOpen(false);
    setSupportTab(tab);
    setSupportOpen(true);
    setSupportError("");
    if (tab === "cases") {
      loadCases();
    }
  };

  const changeSupportTab = (tab) => {
    setSupportTab(tab);
    setSupportError("");
    if (tab === "cases") {
      loadCases();
    }
  };

  const openCaseWorkspace = () => {
    setProfileOpen(false);
    setSupportOpen(false);
    setCaseWorkspaceOpen(true);
    setCaseComment("");
    loadCases();
  };

  const openNewQuestion = () => {
    setCaseWorkspaceOpen(false);
    openSupport("ask");
  };

  const submitCaseComment = async (event, caseId) => {
    event.preventDefault();
    if (!caseComment.trim() || caseCommentRequestsRef.current.has(caseId)) return;
    caseCommentRequestsRef.current.add(caseId);
    setCommentPendingIds([...caseCommentRequestsRef.current]);
    const submittedComment = caseComment;
    const epoch = sessionEpochRef.current;
    caseLoadRef.current.id += 1;
    caseLoadRef.current.controller?.abort();
    setCasesLoading(false);
    setSupportError("");
    try {
      const payload = await requestJson(`/backend/sow/cases/${encodeURIComponent(caseId)}/comments`, {
        method: "POST",
        body: JSON.stringify({ comment: caseComment }),
      });
      if (epoch !== sessionEpochRef.current) return;
      const savedComment = payload?.data?.comment;
      if (savedComment?.id && typeof savedComment.body === "string") {
        caseLoadRef.current.id += 1;
        caseLoadRef.current.controller?.abort();
        setCasesLoading(false);
        setSupportCases((current) => current.map((caseRow) => caseRow.id === caseId
          ? { ...caseRow, answers: [...(caseRow.answers || []).filter((answer) => answer.id !== savedComment.id), savedComment] }
          : caseRow));
      } else {
        await loadCases();
      }
      if (selectedCaseIdRef.current === caseId) {
        setCaseComment((current) => current === submittedComment ? "" : current);
      }
    } catch (requestError) {
      if (epoch === sessionEpochRef.current) setSupportError(requestError.message);
    } finally {
      if (epoch === sessionEpochRef.current) {
        caseCommentRequestsRef.current.delete(caseId);
        setCommentPendingIds([...caseCommentRequestsRef.current]);
      }
    }
  };

  const submitQuestion = async (event) => {
    event.preventDefault();
    if (caseCreateBusyRef.current || !caseSubject.trim() || !caseQuestion.trim()) return;
    caseCreateBusyRef.current = true;
    const epoch = sessionEpochRef.current;
    setCaseBusy(true);
    setSupportError("");
    try {
      const payload = await requestJson("/backend/sow/cases", {
        method: "POST",
        body: JSON.stringify({
          subject: caseSubject,
          question: caseQuestion,
        }),
      });
      if (epoch !== sessionEpochRef.current) return;
      const createdCaseId = payload?.data?.case?.id || "";
      setCaseSubject("");
      setCaseQuestion("");
      setSupportOpen(false);
      setCaseWorkspaceOpen(true);
      await loadCases(createdCaseId);
    } catch (requestError) {
      if (epoch === sessionEpochRef.current) setSupportError(requestError.message);
    } finally {
      caseCreateBusyRef.current = false;
      setCaseBusy(false);
    }
  };

  if (screen === "checking") {
    return <LoadingState />;
  }

  if ((screen === "workspace" || screen === "recovering") && session?.project
    && session.project.slug === requestedProjectSlug) {
    const documentUrl = `${getBackendBaseUrl()}/backend/sow/content/${encodeURIComponent(session.project.slug)}?workspaceApp=sow`;
    return (
      <DealDeskShell activeApp={activeApp} onAppChange={selectWorkspaceApp} projectName={portalConfig.brandName}
        actions={screen === "workspace" && <div className="deal-desk__notification-actions">
          <DealDeskAi key={`${session.project.id}:${session.user?.email || ""}`} project={session.project} user={session.user} requestJson={requestJson} />
          {session.project.slug === "riomax" && <DealDeskNotifications key={`notifications:${session.project.id}:${session.user?.email || ""}`}
            requestJson={requestJson} onOpen={target => {
              if (!target || !["idea", "project", "case"].includes(target.kind)) throw new Error("This notification cannot be opened.");
              const draft = document.querySelector('.deal-desk-projects [data-project-has-detail-draft="true"]');
              if (target.kind === "project" && draft && draft.dataset.projectEntityId !== target.recordId) throw new Error("Save or cancel your current task edit before opening another task.");
              selectWorkspaceApp(target.app);
              if (target.kind === "case") setLegacyPhaseTwoOpened(true);
              setNotificationTarget({ ...target, requestId: `${Date.now()}:${target.commentId}` });
            }} />}
        </div>}
        customTools={activeApp === 'ideas' && session.project.slug === 'riomax' && session.project.planStudioEnabled && notificationTarget?.kind !== 'case'}
        projectLogo={<ProjectWordmark projectSlug={session.project.slug} />}>
        {planRecovery && <PlanStudioRecovery recovery={planRecovery} onRetry={capturePlanRecovery} onClear={clearPlanRecovery} onLogout={logout} />}
        <Helmet>
          <title>{`${DEAL_DESK_APPS.find(app => app.id === activeApp)?.label || 'Workspace'} | ${portalConfig.brandName} | Antrazal`}</title>
          <meta name="robots" content="noindex,nofollow,noarchive" />
        </Helmet>
        {visitedApps.has("sow") && <main className="sow-document-stage" hidden={activeApp !== "sow"}>
          {documentLoading && (
            <div className="sow-document-loading" aria-live="polite">
              <FiFileText aria-hidden="true" />
              <span>Opening protected document</span>
            </div>
          )}
          <iframe
            ref={documentFrameRef}
            className={documentLoading ? "sow-document-frame is-loading" : "sow-document-frame"}
            src={documentUrl}
            title={session.project.title}
            sandbox="allow-scripts allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox"
            allow="clipboard-write"
            allowFullScreen
            referrerPolicy="no-referrer"
            onLoad={() => { setDocumentLoading(false); setDocumentModalOpen(false); }}
          />
        </main>}
        {visitedApps.has("documentation") && <div className="deal-desk__app" hidden={activeApp !== "documentation"}>
          <WorkspaceResources key={`documentation-${session.project.slug}`} mode="documentation" baseUrl={getBackendBaseUrl()} project={session.project} requestJson={requestJson} />
        </div>}
        {visitedApps.has("assets") && <div className="deal-desk__app" hidden={activeApp !== "assets"}>
          <WorkspaceResources key={`assets-${session.project.slug}`} baseUrl={getBackendBaseUrl()} project={session.project} requestJson={requestJson} />
        </div>}
        {visitedApps.has("projects") && <div className="deal-desk__app" hidden={activeApp !== "projects"}>
          {session.project.slug === "riomax"
            ? <WorkspaceProjects key={session.project.id} project={session.project} user={session.user} active={activeApp === "projects"} requestJson={requestJson} baseUrl={getBackendBaseUrl()}
              notificationTarget={notificationTarget?.kind === "project" ? notificationTarget : null} />
            : <section className="deal-desk__empty"><FiColumns aria-hidden="true" /><h2>Project Management is not enabled for this project</h2>
              <button type="button" className="deal-desk__button" onClick={() => openSupport("ask")}>Ask the project team</button></section>}
        </div>}
        {activeApp === "ideas" && session.project.slug !== "riomax" && <section className="deal-desk__empty">
          <FiColumns aria-hidden="true" /><h2>Idea Builder is not enabled for this project</h2>
          <button type="button" className="deal-desk__button" onClick={() => openSupport("ask")}>Ask the project team</button>
        </section>}
        <div className="sow-profile" ref={profileMenuRef} hidden={documentModalOpen && activeApp === "sow"}>
          <button
            type="button"
            className="sow-profile__trigger"
            onClick={() => setProfileOpen((current) => !current)}
            aria-label="Open profile menu"
            aria-expanded={profileOpen}
            aria-controls="sow-profile-menu"
          >
            {getInitials(session.user?.name)}
          </button>
          {profileOpen && (
            <div className="sow-profile__menu" id="sow-profile-menu" role="menu">
              <header>
                <span className="sow-profile__avatar">{getInitials(session.user?.name)}</span>
                <div>
                  <strong>{session.user?.name}</strong>
                  <small>{session.user?.email}</small>
                </div>
              </header>
              <a href="/who-we-are" target="_blank" rel="noreferrer" role="menuitem">
                <FiUser aria-hidden="true" />
                <span><strong>How Antrazal Works</strong><small>Our team and operating approach</small></span>
                <FiExternalLink aria-hidden="true" />
              </a>
              <button type="button" role="menuitem" onClick={openCaseWorkspace}>
                <FiBriefcase aria-hidden="true" />
                <span><strong>Questions / Cases</strong><small>View project conversations</small></span>
              </button>
              <button type="button" role="menuitem" onClick={() => openSupport("ask")}>
                <FiHelpCircle aria-hidden="true" /><span><strong>Project support</strong><small>Ask the project team</small></span>
              </button>
              <button type="button" role="menuitem" onClick={logout} disabled={busy}>
                <FiLogOut aria-hidden="true" />
                <span><strong>Sign out</strong><small>End this secure session</small></span>
              </button>
            </div>
          )}
        </div>
        {caseWorkspaceOpen && (
          <CaseWorkspace
            cases={supportCases}
            comment={caseComment}
            commentBusy={commentPendingIds.includes(selectedCaseId)}
            loading={casesLoading}
            onBack={() => {
              setCaseWorkspaceOpen(false);
              setSupportError("");
            }}
            onCommentChange={setCaseComment}
            onNewQuestion={openNewQuestion}
            onRefresh={() => loadCases(selectedCaseId)}
            onSelectCase={(caseId) => {
              selectedCaseIdRef.current = caseId;
              setSelectedCaseId(caseId);
              setCaseComment("");
              setSupportError("");
            }}
            onSubmitComment={submitCaseComment}
            selectedCaseId={selectedCaseId}
            supportError={supportError}
          />
        )}
        {phaseTwoWorkspaceOpen && session.project.slug === "riomax" && (
          <div className="deal-desk__app deal-desk__ideas" hidden={activeApp !== "ideas"}>
          {session.project.planStudioEnabled && <div hidden={notificationTarget?.kind === "case"}>
            <RiomaxPlanStudio ref={planStudioRef} active={activeApp === 'ideas' && notificationTarget?.kind !== 'case'} focusTarget={notificationTarget?.kind === "idea" ? notificationTarget : null}
              key={`${session.project.id}:${session.user?.email || ""}`} embedded baseUrl={getBackendBaseUrl()} onClose={() => selectWorkspaceApp("home")} />
          </div>}
          {(!session.project.planStudioEnabled || legacyPhaseTwoOpened) &&
          <div hidden={session.project.planStudioEnabled && notificationTarget?.kind !== "case"}>
          <PhaseTwoWorkspace
            currentUser={session.user}
            notificationTarget={notificationTarget?.kind === "case" ? notificationTarget : null}
            embedded active={activeApp === "ideas" && (!session.project.planStudioEnabled || notificationTarget?.kind === "case")}
            onClose={() => selectWorkspaceApp("sow")}
          /></div>}
          </div>
        )}
        <div className={`sow-support ${supportOpen ? "is-open" : ""}`} hidden={documentModalOpen && activeApp === "sow"}>
          {supportOpen && (
            <section className="sow-support-panel" id="sow-support-panel" aria-label="Project support">
              <header className="sow-support-header">
                <div>
                  <span>Project support</span>
                  <h2>
                    {supportTab === "ask"
                      ? "Ask about this page"
                      : "Project cases"}
                  </h2>
                </div>
                <button type="button" onClick={() => setSupportOpen(false)} aria-label="Close project support">
                  <FiX aria-hidden="true" />
                </button>
              </header>
              <div className="sow-support-tabs" role="tablist" aria-label="Project support options">
                <button
                  type="button"
                  role="tab"
                  aria-selected={supportTab === "ask"}
                  className={supportTab === "ask" ? "is-active" : ""}
                  onClick={() => changeSupportTab("ask")}
                >
                  <FiMessageSquare aria-hidden="true" />
                  Ask a question
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={supportTab === "cases"}
                  className={supportTab === "cases" ? "is-active" : ""}
                  onClick={() => changeSupportTab("cases")}
                >
                  <FiInbox aria-hidden="true" /> Project cases
                </button>
              </div>
              <div className="sow-support-content">
                {supportTab === "ask" ? (
                  <form className="sow-question-form" onSubmit={submitQuestion}>
                    <p>Ask any question about this page or project. Each question creates a separate Case, and you can raise another whenever you need one.</p>
                    <label htmlFor="sow-case-subject">Subject</label>
                    <input
                      id="sow-case-subject"
                      type="text"
                      value={caseSubject}
                      onChange={(event) => setCaseSubject(event.target.value)}
                      placeholder="What do you need help with?"
                      maxLength={160}
                      required
                      disabled={caseBusy}
                    />
                    <label htmlFor="sow-case-question">Your question</label>
                    <textarea
                      id="sow-case-question"
                      value={caseQuestion}
                      onChange={(event) => setCaseQuestion(event.target.value)}
                      placeholder="What would you like the project team to clarify on this page?"
                      maxLength={8000}
                      required
                      disabled={caseBusy}
                    />
                    {supportError && <div className="sow-support-error" role="alert">{supportError}</div>}
                    <button
                      type="submit"
                      className="sow-send-question"
                      disabled={caseBusy || !caseSubject.trim() || !caseQuestion.trim()}
                    >
                      {caseBusy ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiSend aria-hidden="true" />}
                      {caseBusy ? "Sending" : "Send as a Case"}
                    </button>
                  </form>
                ) : (
                  <div className="sow-cases-view">
                    <div className="sow-cases-toolbar">
                      <span>{supportCases.length} {supportCases.length === 1 ? "case" : "cases"}</span>
                      <button type="button" onClick={() => loadCases()} disabled={casesLoading} aria-label="Refresh cases">
                        <FiRefreshCw className={casesLoading ? "sow-spin" : ""} aria-hidden="true" />
                      </button>
                    </div>
                    {supportError && <div className="sow-support-error" role="alert">{supportError}</div>}
                    {casesLoading && !supportCases.length ? (
                      <div className="sow-cases-empty"><FiRefreshCw className="sow-spin" aria-hidden="true" /><span>Loading your cases</span></div>
                    ) : !supportCases.length && !supportError ? (
                      <div className="sow-cases-empty"><FiInbox aria-hidden="true" /><strong>No cases yet</strong><span>Your project questions and answers will appear here.</span></div>
                    ) : (
                      <div className="sow-case-list">
                        {supportCases.map((caseRow) => {
                          const expanded = expandedCaseId === caseRow.id;
                          return (
                            <article className="sow-case-item" key={caseRow.id}>
                              <button
                                type="button"
                                className="sow-case-summary"
                                onClick={() => setExpandedCaseId(expanded ? "" : caseRow.id)}
                                aria-expanded={expanded}
                              >
                                <span className="sow-case-number">Case {caseRow.caseNumber}</span>
                                <span className={`sow-case-status ${caseStatusClass(caseRow.status)}`}>{caseRow.status}</span>
                                <strong>{caseRow.subject}</strong>
                                <small><FiClock aria-hidden="true" /> Updated {formatCaseDate(caseRow.updatedAt)}</small>
                                {expanded ? <FiChevronUp className="sow-case-chevron" aria-hidden="true" /> : <FiChevronDown className="sow-case-chevron" aria-hidden="true" />}
                              </button>
                              {expanded && (
                                <div className="sow-case-thread">
                                  <div className="sow-case-question">
                                    <span>Your question</span>
                                    <p>{caseRow.question}</p>
                                    <small>{formatCaseDate(caseRow.createdAt)}</small>
                                  </div>
                                  {caseRow.answers?.length ? (
                                    <div className="sow-case-answers">
                                      <h3>Answers</h3>
                                      {caseRow.answers.map((answer) => (
                                        <div className="sow-case-answer" key={answer.id}>
                                          <strong>{answer.author}</strong>
                                          <p>{answer.body}</p>
                                          <small>{formatCaseDate(answer.createdAt)}</small>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="sow-awaiting-answer">The project team has your question. An answer will appear here.</div>
                                  )}
                                </div>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <footer className="sow-support-footer">
                <span><strong>{session.user?.name}</strong><small>{session.user?.email}</small></span>
                <button type="button" onClick={logout} disabled={busy}>
                  <FiLogOut aria-hidden="true" /> Sign out
                </button>
              </footer>
            </section>
          )}
          <button
            type="button"
            className={`sow-support-launcher ${!supportOpen ? "is-promoting" : ""}`}
            onClick={() => (supportOpen ? setSupportOpen(false) : openSupport("ask"))}
            aria-label={supportOpen ? "Close project support" : "Ask a question about this page"}
            aria-expanded={supportOpen}
            aria-controls="sow-support-panel"
          >
            {supportOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <>
                <span>Any questions about this page?</span>
                <FiHelpCircle aria-hidden="true" />
              </>
            )}
          </button>
        </div>
      </DealDeskShell>
    );
  }

  if (screen === "recovery" && planRecovery && requestedProjectSlug === "riomax") {
    return <PlanStudioRecovery recovery={planRecovery} onRetry={capturePlanRecovery} onClear={clearPlanRecovery} onLogout={logout} />;
  }

  return (
    <div className="sow-auth-shell">
      <Helmet>
        <title>{portalConfig.pageTitle}</title>
        <meta name="robots" content="noindex,nofollow,noarchive" />
      </Helmet>
      <header className="sow-auth-header">
        <div className="sow-auth-brand" aria-label={`Antrazal and ${portalConfig.brandName} Engagement Workspace`}>
          <a className="sow-auth-logo" href="/" aria-label="Antrazal home">
            <BrandWordmark alt="Antrazal" fallback="Antrazal" src={antrazalWordmark} />
          </a>
          <span className="sow-auth-brand__divider" aria-hidden="true" />
          <ProjectWordmark className="sow-client-wordmark" projectSlug={requestedProjectSlug} />
          <span className="sow-auth-brand__divider" aria-hidden="true" />
          <strong className="sow-auth-brand__name">Engagement Workspace</strong>
        </div>
        <nav className="sow-auth-navigation" aria-label="Antrazal">
          <a href="/case-studies">Case Studies</a>
          <a href="/#customer-stories">Stories</a>
          <a href="/who-we-are">Who We Are</a>
        </nav>
        <span className="sow-protected-label"><FiLock aria-hidden="true" /> Protected client access</span>
      </header>
      <main className="sow-auth-main">
        <section className="sow-auth-panel" aria-labelledby="sow-auth-title">
          <div className="sow-auth-kicker"><FiShield aria-hidden="true" /> {portalConfig.workspaceName}</div>
          {screen === "email" ? (
            <>
              <h1 id="sow-auth-title">Open your engagement workspace</h1>
              <p>Use the email address approved for this statement of work. We will send a one-time verification code.</p>
              <form onSubmit={requestOtp} className="sow-auth-form">
                <label htmlFor="sow-email">Work email</label>
                <div className="sow-input-wrap">
                  <FiMail aria-hidden="true" />
                  <input
                    id="sow-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="name@company.com"
                    autoComplete="email"
                    required
                    maxLength={254}
                    disabled={busy}
                  />
                </div>
                {error && <div className="sow-form-error" role="alert">{error}</div>}
                <button type="submit" className="sow-primary-command" disabled={busy || !email.trim()}>
                  {busy ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiArrowRight aria-hidden="true" />}
                  <span>{busy ? "Sending code" : "Continue securely"}</span>
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 id="sow-auth-title">Check your email</h1>
              <p>Enter the six-digit code sent to <strong>{maskedEmail}</strong>. The code expires in 10 minutes.</p>
              <form onSubmit={verifyOtp} className="sow-auth-form">
                <label htmlFor="sow-otp">Verification code</label>
                <input
                  id="sow-otp"
                  className="sow-otp-input"
                  type="text"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  autoFocus
                  disabled={busy}
                />
                {error && <div className="sow-form-error" role="alert">{error}</div>}
                <button type="submit" className="sow-primary-command" disabled={busy || otp.length !== 6}>
                  {busy ? <FiRefreshCw className="sow-spin" aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
                  <span>{busy ? "Verifying" : "Open workspace"}</span>
                </button>
                <div className="sow-otp-actions">
                  <button type="button" onClick={returnToEmail} disabled={busy}>Use another email</button>
                  <button type="button" onClick={requestOtp} disabled={busy || secondsUntilResend > 0}>
                    {secondsUntilResend > 0 ? `Resend in ${secondsUntilResend}s` : "Resend code"}
                  </button>
                </div>
              </form>
            </>
          )}
          <div className="sow-security-note">
            <FiLock aria-hidden="true" />
            <span>Access is verified against the project assignment maintained in Salesforce.</span>
          </div>
        </section>
        <StoryLinks />
      </main>
      <footer className="sow-auth-footer">
        <span>Private and confidential</span>
        <span>© {new Date().getFullYear()} Antrazal Solutions Pvt. Ltd.</span>
      </footer>
    </div>
  );
}
